import type { SignInState } from "../../../shared/users/signInState";
import { baseApi } from "./baseApi";

// ──────────────────────────── Types ────────────────────────────

export type WlmAssetStatus = "in_stock" | "assigned" | "in_repair" | "retired";

export type WlmSectorId = "senior-living" | "information-technology" | "healthcare" | "general";

export interface WorkforcePackInstall {
  sectorId: string;
  customized: boolean;
  installedAt?: string;
  opsPortalId: string | null;
  /** Wave 1 execution toggles — when on, lifecycle runs execute the action
   * (access/seats/assets) instead of only filing a ticket. Off by default. */
  autoProvisionAccess: boolean;
  autoSeats: boolean;
  autoAssets: boolean;
  /** Wave 2 approval gate — when on, lifecycle runs defer to an admin
   * approve/reject decision instead of executing inline. Off by default. */
  requireApproval: boolean;
  /** Wave 2 account lifecycle toggle — when on, offboarding disables the
   * user's Entra account and onboarding re-enables it. Off by default. */
  autoDisableAccounts: boolean;
  /** SoD enforcement mode (2e) — "warn" audits + allows a conflicting role
   * assignment, "block" rejects it. "off" by default. */
  sodMode: WlmSodMode;
  /** Entra manager write-back toggle — when on, changing a person's manager
   * here also updates their manager in Microsoft Entra. Off by default. */
  syncManagerToEntra: boolean;
}

export interface WlmDepartmentRole {
  id: string;
  name: string;
  source: string;
  createdAt?: string;
}

export type WlmTaskCategory = "access" | "asset" | "other";

export interface WlmOnboardingTask {
  id: string;
  title: string;
  description: string;
  category: WlmTaskCategory;
  sortOrder: number;
  source: string;
}

export type WlmTicketPriority = "critical" | "high" | "medium" | "low";

/** A reusable ticket template, attachable to many departments' onboarding. */
export interface WlmTicketTemplate {
  id: string;
  title: string;
  description: string;
  category: WlmTaskCategory;
  priority: WlmTicketPriority;
  /** Soft refs into the ops portal; null = no group/request-type wiring. */
  portalGroupId: string | null;
  requestTypeId: string | null;
  sortOrder: number;
  /** Departments this template is attached to. */
  departmentIds: string[];
  /**
   * Count of references that block deletion — the department onboarding
   * checklists this template is attached to (mirrors the server-side delete
   * guard). When > 0 the UI surfaces the "in use" state instantly, without a
   * delete round-trip (#944).
   */
  usageCount: number;
}

export interface WlmTicketTemplateInput {
  title: string;
  description?: string;
  category?: WlmTaskCategory;
  priority?: WlmTicketPriority;
  portalGroupId?: string | null;
  requestTypeId?: string | null;
  departmentIds?: string[];
}

export type WlmTicketTemplatePatch = Partial<WlmTicketTemplateInput> & { sortOrder?: number };

export interface WlmDepartment {
  id: string;
  name: string;
  description: string;
  source: string;
  packSlug: string | null;
  roles: WlmDepartmentRole[];
  onboardingTasks: WlmOnboardingTask[];
  /** Party & Employment core — count of active employments in this department. */
  memberCount: number;
  createdAt?: string;
  updatedAt?: string;
}

export type WlmAttributeType = "text" | "number" | "date" | "reference" | "image";

export interface WlmAssetTypeAttribute {
  id: string;
  name: string;
  attributeType: WlmAttributeType;
  sortOrder: number;
  createdAt?: string;
  /**
   * Only meaningful when attributeType === "reference": the target asset
   * type an attribute value must belong to, or null/undefined for "any
   * asset type". Optional on the frontend type (rather than mirroring the
   * server's always-present `string | null`) so existing text/number/date
   * fixtures across the test suite don't all need updating.
   */
  referenceAssetTypeId?: string | null;
}

export interface WlmAssetType {
  id: string;
  name: string;
  source: string;
  packSlug: string | null;
  attributes: WlmAssetTypeAttribute[];
  /** Blob name of the type's uploaded image, or null — used as the fallback tile for assets of this type without their own photo. */
  imageBlobName?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface WorkforcePackPayload {
  install: WorkforcePackInstall | null;
  departments: WlmDepartment[];
  assetTypes: WlmAssetType[];
}

export interface WlmAsset {
  id: string;
  assetTypeId: string;
  name: string;
  attributes: Record<string, string>;
  status: WlmAssetStatus;
  conditionNote: string;
  /**
   * Canonical holder. A pre-start person has no agent, so this is the only id
   * that names them — match on it first when resolving who holds an asset.
   */
  assignedToPartyId: string | null;
  /** Denormalized mirror of the holder's agent; NULL for a pre-start holder. */
  assignedToAgentId: string | null;
  assignedAt: string | null;
  /** Present on list responses (joined from the asset type). */
  assetTypeName?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Camelized `PersonDepartmentSummary` — one active-employment department for a listPeople row. */
export interface WlmPersonDepartmentSummary {
  id: string;
  name: string;
  jobTitle: string;
}

export interface WlmPerson {
  id: string;
  userId: string;
  name: string;
  email: string;
  /** Base application role slug (e.g. "agent", "admin"). */
  role: string;
  /** Custom role display name, when one is assigned. */
  customRoleName: string | null;
  isActive: boolean | null;
  lastLoginAt: string | null;
  /** When the agent record was created — the "joined" date on lifecycle views. */
  createdAt?: string;
  assetCount: number;
  /** Party & Employment core — additive fields, null/empty until a party sync has run. */
  partyId: string | null;
  departments: WlmPersonDepartmentSummary[];
  /** Whether they have signed in yet — drives the Pre-start label (#1414).
   * Optional so older payloads/fixtures fall back to the role check. */
  signInState?: SignInState;
}

export interface WlmAuditEntry {
  id: string;
  entityType: string;
  entityId: string | null;
  action: string;
  actorUserId: string | null;
  actorEmail: string | null;
  payload: Record<string, unknown>;
  createdAt: string;
}

export interface WlmAccessEntry {
  roleType: string;
  roleName: string | null;
  assignedBy: string | null;
  assignedAt: string;
  status: string | null;
}

export interface WlmAccessHistoryEntry {
  id: string;
  targetEmail: string;
  targetUserObjectId: string | null;
  actorId: string | null;
  action: string;
  fieldName: string | null;
  previousValue: string | null;
  newValue: string | null;
  createdAt: string;
}

export type WlmEmploymentType = "full_time" | "part_time" | "contract" | "agency" | "volunteer";

/** Camelized `wlm_person_details` row — personal fields never surface outside this record. */
export interface WlmPersonDetails {
  id: string;
  email: string;
  agentId: string | null;
  name: string;
  /** The person's one mobile number (texts, phone sign-in). */
  phone: string;
  /** How it got there: typed in WLM, or from Microsoft Entra. */
  phoneSource?: "typed" | "entra" | null;
  /** Entra's mobile as Entra has it. */
  phoneEntra?: string | null;
  phoneSetBy?: string | null;
  phoneSetAt?: string | null;
  dateOfBirth: string | null;
  address: string;
  emergencyContactName: string;
  emergencyContactPhone: string;
  employmentType: WlmEmploymentType | null;
  jobTitle: string;
  startDate: string | null;
  notes: string;
  createdAt?: string;
  updatedAt?: string;
}

/** Camelized `PartySummaryRow` — the party's identity fields only (name/email live on `agent`). */
export interface WlmPartySummary {
  id: string;
  partyType: string;
  status: string;
}

/** Camelized `EmploymentWithDeptRow` — one wlm_employments row joined to its department name. */
export interface WlmEmployment {
  id: string;
  departmentId: string | null;
  departmentName: string | null;
  jobTitle: string;
  employmentType: WlmEmploymentType | null;
  status: string;
  startDate: string | null;
  endDate: string | null;
  isPrimary: boolean;
  /** When the department entry was added (real timestamptz — drives the "since" label). */
  createdAt: string;
  locationId: string | null;
  locationName: string | null;
}

/** Camelized `wlm_licenses` row, enriched with seat-usage counts by listLicenses/updateLicense/etc. */
export type WlmLicenseKind = "per_seat" | "company_wide";

export interface WlmLicense {
  id: string;
  name: string;
  vendor: string;
  kind: WlmLicenseKind;
  seatsTotal: number | null;
  renewalDate: string | null;
  monthlyCost: string | null;
  ownerDepartmentId: string | null;
  /** Slug of the UAM connector this license is mapped to for reconciliation
   * (e.g. "okta"), or null when unmapped. */
  connectorSlug: string | null;
  /** Group/role id within that connector's app to scope the reconciled
   * roster to, or null when any account on the connector counts. */
  connectorRef: string | null;
  notes: string;
  seatsUsed: number;
  /** null for company_wide (no seat cap to be free against). */
  seatsFree: number | null;
  createdAt?: string;
  updatedAt?: string;
}

/** One active `wlm_license_seats` row, joined to the holder's party name/email. */
export interface WlmLicenseSeat {
  id: string;
  partyId: string;
  agentId: string | null;
  partyName: string;
  partyEmail: string;
  grantedAt: string;
}

/** One app's access for a person — the person's roles in that Skypoint app. */
export interface WlmPersonAppAccess {
  appSlug: string;
  displayName: string;
  roles: string[];
}

/** One subscription entry on the person profile's `subscriptions` list. */
export interface WlmSubscriptionSummary {
  licenseId: string;
  licenseName: string;
  kind: WlmLicenseKind;
  grantedAt: string;
}

export interface WlmPersonProfile {
  agent: Omit<WlmPerson, "assetCount" | "partyId" | "departments">;
  /** True for a pre-start / synced-only party with no agent yet — the profile
   * is read-only (access/assets empty, mutating actions hidden). */
  isPreStart: boolean;
  /** Labels only (Pre-start banner/badge); isPreStart still gates the app-access fetch. */
  signInState?: SignInState;
  details: WlmPersonDetails | null;
  assetsHeld: WlmAsset[];
  assetTimeline: WlmAuditEntry[];
  currentAccess: WlmAccessEntry[];
  accessHistory: WlmAccessHistoryEntry[];
  /** Party & Employment core — additive fields, null/empty until a party sync has run. */
  party: WlmPartySummary | null;
  employments: WlmEmployment[];
  /**
   * Licenses & subscriptions — additive field, may be undefined on a stale
   * cached response from before this shipped. Always guard reads with
   * `?? []` rather than assuming presence.
   */
  subscriptions?: WlmSubscriptionSummary[];
  /** Org hierarchy (5d) — the person's manager + direct-report count. Additive;
   * may be undefined on a stale cached response. */
  manager?: { partyId: string; name: string } | null;
  directReportCount?: number;
}

/** POST /workforce/lifecycle/{onboarding,offboarding} response shape. */
export interface WlmLifecycleResult {
  parentItemKey: string;
  parentItemId: string;
  portalKey: string;
  taskCount: number;
}

/** POST /workforce/lifecycle/move-preview response shape — no side effects. */
/** Rule-driven access a move will provision, named in the preview (#1418). */
export interface WlmMoveAccessGrant {
  appSlug: string;
  appName: string;
  roleName: string;
}

export interface WlmMovePreviewResult {
  party: { id: string; name: string };
  from: { id: string; name: string } | null;
  to: { id: string; name: string };
  jobTitle: { current: string; proposed: string };
  grants: string[];
  revokes: string[];
  /** Access the target department's grant rules will provision. Empty when
   * Auto-provision access is off — nothing will be provisioned. */
  accessGrants: WlmMoveAccessGrant[];
  /** Require approval is on: `accessGrants` wait for an admin's approval. */
  accessRequiresApproval?: boolean;
  /** #1437: apps the move takes away — the old department's rules granted them, the new one's don't. */
  accessRevokes?: Array<{ appSlug: string; appName: string }>;
}

/** POST /workforce/lifecycle/move response shape. */
export interface WlmMoveResult extends WlmLifecycleResult {
  employmentId: string;
  grants: number;
  revokes: number;
  /** Birthright grants actually provisioned by the move (#1418). */
  accessGranted: number;
  /** Grants and revokes held for Lifecycle › Approvals (Require approval is on). */
  accessPending?: number;
  /** #1437: old-department access removed by the move. */
  accessRevoked?: number;
}

/** One app grant as the admin reads it: "skyLearn · Administrator". */
export interface WlmGrantSummary {
  appName: string;
  roleName: string;
}

/** What a department's access grant rules did when someone was added (#1418). */
export interface WlmGrantOutcome {
  granted: WlmGrantSummary[];
  /** Held for Lifecycle › Approvals because Require approval is on. */
  pending: WlmGrantSummary[];
  failed: WlmGrantSummary[];
}

export interface WlmMoveInput {
  employmentId: string;
  toDepartmentId: string;
  jobTitle?: string;
}

export type WlmLifecycleRunKind = "onboarding" | "offboarding" | "move";

export interface WlmLifecycleRunTask {
  itemKey: string;
  title: string;
  statusName: string | null;
  statusCategory: string | null;
}

export interface WlmLifecycleRunParent {
  id: string;
  itemKey: string;
  title: string;
  statusId: string | null;
  portalId: string;
  statusName: string | null;
  statusCategory: string | null;
  portalKey: string | null;
}

/** GET /workforce/lifecycle/runs row shape — one onboarding/offboarding run. */
export interface WlmLifecycleRun {
  id: string;
  kind: WlmLifecycleRunKind;
  startedAt: string;
  actorEmail: string | null;
  subjectName: string | null;
  agentId: string | null;
  /** The email the run's subject was onboarded/moved with (nullable on older runs). */
  subjectEmail?: string | null;
  parentItemKey: string | null;
  portalKey: string | null;
  parent: WlmLifecycleRunParent | null;
  tasks: WlmLifecycleRunTask[];
  progress: { total: number; done: number };
}

// ──────────────────────────── Request payloads ────────────────────────────

export interface WlmDepartmentInput {
  name: string;
  description?: string;
}

export interface WlmDepartmentPatch {
  name?: string;
  description?: string;
}

export interface WlmOnboardingTaskInput {
  title: string;
  description?: string;
  category?: WlmTaskCategory;
}

export interface WlmOnboardingTaskPatch {
  title?: string;
  description?: string;
  category?: WlmTaskCategory;
  sortOrder?: number;
}

export interface WlmAssetInput {
  assetTypeId: string;
  name: string;
  attributes?: Record<string, string>;
  status?: WlmAssetStatus;
  conditionNote?: string;
}

export interface WlmAssetPatch {
  name?: string;
  attributes?: Record<string, string>;
  status?: WlmAssetStatus;
  conditionNote?: string;
}

/** POST /workforce/assets/bulk row — mirrors assetBulkImportRowSchema. */
export interface WlmAssetBulkImportRow {
  name: string;
  attributes?: Record<string, string>;
}

export interface WlmAssetBulkImportInput {
  assetTypeId: string;
  rows: WlmAssetBulkImportRow[];
}

/**
 * POST /people/:agentId/employments payload — a direct, ticket-free path to
 * place an existing agent (one who predates WLM) into a department without
 * running the onboarding wizard.
 */
export interface WlmAddEmploymentInput {
  departmentId: string;
  jobTitle?: string;
  employmentType?: WlmEmploymentType;
  startDate?: string;
  /** Where they work. A team is a (location, department) pair, so an
   *  employment with no location is unreachable by team-targeted content. */
  locationId?: string;
}

/**
 * A location: a site people work at. Created here, from skyEmployee's Users &
 * organisation screen, or by directory sync — all three write the same
 * `wlm_locations` row.
 */
export interface WlmLocation {
  id: string;
  name: string;
  /** 'entra' came from directory sync; 'custom' was made by an admin. */
  source: "custom" | "entra";
  status: "active" | "archived";
  createdAt: string;
  updatedAt: string;
}

/** The people endpoint's largest page (`pageSize` max in shared/validation/workforce). */
const PEOPLE_PAGE_MAX = 200;

export interface WlmPeopleFilter {
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface WlmAuditFilter {
  entityType?: "pack" | "department" | "role" | "asset_type" | "attribute" | "asset";
  entityId?: string;
  page?: number;
  pageSize?: number;
}

/**
 * Optional personal-details fields, shared by the onboarding wizard's
 * "details" section and the standalone PATCH /people/:agentId/details
 * endpoint. jobTitle/startDate are deliberately NOT here — they're captured
 * at the top level of onboarding input (roleName/startDate) and persisted
 * into wlm_person_details by the backend without duplicating the field.
 */
export interface WlmPersonDetailsInput {
  phone?: string;
  dateOfBirth?: string;
  address?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  /** Replace a number typed in WLM with Microsoft Entra's. */
  useEntraPhone?: true;
  employmentType?: WlmEmploymentType;
  notes?: string;
}

/** Full PATCH /people/:agentId/details payload — jobTitle lives here too (edited from the profile card). */
export interface WlmPersonDetailsPatch extends WlmPersonDetailsInput {
  jobTitle?: string;
}

/**
 * Narrows which of a department's onboarding task templates create children,
 * and appends one-off tasks — mirrors OnboardingTaskOverrides on the backend
 * (server/services/workforce/workforceLifecycleService.ts). `include` only
 * matters in template mode (department has ≥1 configured task) — omit it to
 * use every template task; `extras` are always appended regardless of mode.
 */
export interface WlmOnboardingTaskOverrides {
  include?: string[];
  extras?: { title: string; category?: WlmTaskCategory }[];
}

export interface WlmOnboardingInput {
  name: string;
  email: string;
  departmentId: string;
  roleName?: string;
  startDate?: string;
  details?: WlmPersonDetailsInput;
  taskOverrides?: WlmOnboardingTaskOverrides;
  /** Set when this run was started from a ticket lifecycle suggestion — the server marks it accepted. */
  suggestionId?: string;
  /** Where they'll work, when Add user chose a location. */
  locationId?: string;
}

/** One birthright-access grant the onboarding wizard's preview step would apply. */
export interface WlmOnboardingGrant {
  appSlug: string;
  appName: string;
  roleName: string;
}

/** GET-shaped POST /workforce/lifecycle/onboarding/preview-grants response — dry-run. */
export interface WlmOnboardingGrantPreview {
  grants: WlmOnboardingGrant[];
}

/** GET /workforce/lifecycle/asset-tickets row — one ticket with its linked assets. */
export interface WlmAssetLinkedTicket {
  workItemId: string;
  itemKey: string;
  title: string;
  statusName: string | null;
  statusCategory: string | null;
  portalKey: string | null;
  updatedAt: string;
  assets: { id: string; name: string; status: string }[];
}

export interface WlmLifecycleRunsFilter {
  page?: number;
  pageSize?: number;
  kind?: WlmLifecycleRunKind;
}

export interface WlmWorkforceSettingsInput {
  opsPortalId?: string | null;
  autoProvisionAccess?: boolean;
  autoSeats?: boolean;
  autoAssets?: boolean;
  requireApproval?: boolean;
  autoDisableAccounts?: boolean;
  sodMode?: WlmSodMode;
  syncManagerToEntra?: boolean;
}

/**
 * Camelized `wlm_lifecycle_approvals` row — mirrors the server's
 * `ApprovalRow` (server/services/workforce/workforceApprovalsService.ts).
 * `kind` stays a plain string (rather than WlmLifecycleRunKind) because the
 * server column isn't narrowed to an enum.
 */
export interface WlmLifecycleApproval {
  id: string;
  runParentItemKey: string;
  kind: string;
  subjectName: string | null;
  subjectAgentId: string | null;
  status: string;
  pendingStepCount: number;
  requestedByEmail: string | null;
  requestedAt: string | null;
  decidedByEmail: string | null;
  decidedAt: string | null;
  /** One entry per app still waiting, each decided on its own (#1418). */
  groups?: WlmApprovalGroup[];
}

/** One app's held grants within an approval, as this caller sees them (#1418). */
export interface WlmApprovalGroup {
  /** The app slug, or "other" for license seats, assets and accounts. */
  key: string;
  appSlug: string | null;
  appName: string | null;
  /** What approving would do ("Grant Administrator"). */
  items: string[];
  /** Named approvers, or null when any Workforce admin may decide. */
  approvers: string[] | null;
  canDecide: boolean;
  blockedReason?: string;
}

/** Settings › Approvers: who approves, and for which apps (#1418). */
export interface WlmApproverGroup {
  id: string;
  name: string;
  members: string[];
  appSlugs: string[];
}

export interface WlmApproverGroupInput {
  name: string;
  members: string[];
  appSlugs: string[];
}

export interface WlmApprovableApp {
  appSlug: string;
  displayName: string;
}

export interface WlmApproverCandidate {
  email: string;
  name: string;
}

export interface WlmLicenseInput {
  name: string;
  vendor?: string;
  kind: WlmLicenseKind;
  seatsTotal?: number;
  renewalDate?: string;
  monthlyCost?: string;
  ownerDepartmentId?: string;
  connectorSlug?: string | null;
  connectorRef?: string | null;
  notes?: string;
}

export type WlmLicensePatch = Partial<WlmLicenseInput>;

export interface WlmLicenseListFilter {
  search?: string;
  kind?: WlmLicenseKind;
  page?: number;
  pageSize?: number;
}

/**
 * Camelized `wlm_access_grants` row (Wave 2 access grant-map) — the server
 * (`AccessGrantRow` in server/services/workforce/execution/resolvers.ts)
 * returns raw SQL column names (snake_case); `getAccessGrants`/
 * `createAccessGrant`'s `transformResponse` maps to this shape. Either
 * `departmentId` or `roleName` (or both) may be null — a null column is a
 * wildcard match in `buildGrantSteps`.
 */
export type WlmGrantConditionAttribute =
  | "department"
  | "jobTitle"
  | "employmentType"
  | "emailDomain";
export type WlmGrantConditionOperator = "equals" | "contains" | "in";

/** One conditional predicate on a new-hire subject; `value` is string[] only for `in`. */
export interface WlmGrantCondition {
  attribute: WlmGrantConditionAttribute;
  operator: WlmGrantConditionOperator;
  value: string | string[];
}

export interface WlmAccessGrant {
  id: string;
  departmentId: string | null;
  roleName: string | null;
  appSlug: string;
  roleKey: string;
  conditions: WlmGrantCondition[];
  createdAt: string;
}

export interface WlmAccessGrantInput {
  departmentId?: string | null;
  roleName?: string | null;
  appSlug: string;
  roleKey: string;
  conditions?: WlmGrantCondition[];
}

/** One CSV bulk-import row (flat shape + employmentType); names resolved server-side. */
export interface WlmAccessGrantImportRow {
  department?: string;
  role?: string;
  employmentType?: string;
  app: string;
  roleKey: string;
}

/** One provisioning-target app + its reachable role options, for the grant editor's dependent dropdowns. */
export interface WlmGrantableApp {
  appSlug: string;
  displayName: string;
  roles: { key: string; name: string }[];
}

/** Wire shape of `AccessGrantRow` (server/services/workforce/execution/resolvers.ts) — snake_case raw SQL columns. */
interface WlmAccessGrantWire {
  id: string;
  instance_id: string;
  department_id: string | null;
  role_name: string | null;
  app_slug: string;
  role_key: string;
  conditions: WlmGrantCondition[] | null;
  created_at: string;
}

function camelizeAccessGrant(row: WlmAccessGrantWire): WlmAccessGrant {
  return {
    id: row.id,
    departmentId: row.department_id,
    roleName: row.role_name,
    appSlug: row.app_slug,
    roleKey: row.role_key,
    conditions: Array.isArray(row.conditions) ? row.conditions : [],
    createdAt: row.created_at,
  };
}

// ──────────────────────────── Service Desk seams ────────────────────────────

/** GET /workforce/asset-options row — lightweight picker option for the asset_ref field renderer. */
export interface WlmAssetOption {
  id: string;
  name: string;
  assetTypeName: string;
  status: WlmAssetStatus;
}

export interface WlmReporterAssetSummary {
  id: string;
  name: string;
  status: WlmAssetStatus;
  assetTypeName: string;
}

/** GET /workforce/reporter-context response — job title + assets only (same privacy rule as elsewhere: no personal details). */
export interface WlmReporterContext {
  person: { agentId: string; name: string; email: string; jobTitle: string } | null;
  assetsHeld: WlmReporterAssetSummary[];
  openTickets: number;
}

export interface WlmLinkedAssetSummary {
  id: string;
  name: string;
  status: WlmAssetStatus;
  conditionNote: string;
  assetTypeName: string;
  assignedToAgentId: string | null;
}

/** GET /workforce/ticket-links row — one work_item_asset_links edge with the joined asset. */
export interface WlmWorkItemAssetLink {
  id: string;
  asset: WlmLinkedAssetSummary;
}

/** GET /workforce/assets/:id/tickets row. */
export interface WlmTicketForAsset {
  itemKey: string;
  title: string;
  statusName: string | null;
  statusCategory: string | null;
  portalKey: string | null;
  createdAt: string;
}

/** The referenced/referencing asset's identity fields, as embedded in a WlmAssetDetail reference row. */
export interface WlmAssetReferenceTarget {
  id: string;
  name: string;
  status: WlmAssetStatus;
  assetTypeName: string;
}

/** One outgoing reference from an asset's own attributes — `asset` is null (and `danglingId` set) when the referenced asset no longer exists. */
export interface WlmAssetReference {
  attributeName: string;
  asset: WlmAssetReferenceTarget | null;
  danglingId?: string;
}

/** One incoming reference — another asset whose reference attribute points at this one. */
export interface WlmAssetReferencedBy {
  attributeName: string;
  asset: WlmAssetReferenceTarget;
}

/** GET /workforce/assets/:assetId response — the asset detail drawer's full payload. */
export interface WlmAssetDetail {
  asset: WlmAsset;
  references: WlmAssetReference[];
  referencedBy: WlmAssetReferencedBy[];
  tickets: WlmTicketForAsset[];
}

/** One node in a GET /workforce/assets/:assetId/graph traversal — `depth` is the BFS hop count from the queried root (0). */
export interface WlmAssetGraphNode {
  id: string;
  name: string;
  status: WlmAssetStatus;
  assetTypeName: string;
  depth: number;
}

/** One edge in a graph traversal — `fromId`'s reference attribute `attributeName` points at `toId`. */
export interface WlmAssetGraphEdge {
  fromId: string;
  toId: string;
  attributeName: string;
}

/** GET /workforce/assets/:assetId/graph response — the graph explorer's payload. */
export interface WlmAssetGraph {
  nodes: WlmAssetGraphNode[];
  edges: WlmAssetGraphEdge[];
  /** True when traversal stopped early at the 150-node cap — more of the graph exists beyond what's shown. */
  truncated: boolean;
}

/** One repeated `attr=name:value` query param for GET /workforce/assets (and its /export twin). */
export interface WlmAssetAttributeFilter {
  name: string;
  value: string;
}

export type WlmAssetSort = "name" | "createdAt" | "status";
export type WlmSortOrder = "asc" | "desc";

/** Query params accepted by GET /workforce/assets and GET /workforce/assets/export. */
export interface WlmAssetListFilter {
  assetTypeId?: string;
  status?: WlmAssetStatus;
  search?: string;
  assignedToAgentId?: string;
  attributes?: WlmAssetAttributeFilter[];
  sort?: WlmAssetSort;
  order?: WlmSortOrder;
  page?: number;
  pageSize?: number;
}

/**
 * Builds the wire query string for the asset list/export endpoints. Not
 * passed as an RTK Query `params` object because attribute filters are
 * repeated `attr=name:value` params — `URLSearchParams`'s object
 * constructor (what fetchBaseQuery uses under the hood for `params`)
 * stringifies array values with a comma join instead of repeating the key,
 * so both the query endpoint and the plain-fetch export util build the
 * query string here with `URLSearchParams#append`.
 */
export function buildAssetListQueryString(filter?: WlmAssetListFilter | null): string {
  const qs = new URLSearchParams();
  if (filter?.assetTypeId) qs.set("assetTypeId", filter.assetTypeId);
  if (filter?.status) qs.set("status", filter.status);
  if (filter?.search) qs.set("search", filter.search);
  if (filter?.assignedToAgentId) qs.set("assignedToAgentId", filter.assignedToAgentId);
  if (filter?.sort) qs.set("sort", filter.sort);
  if (filter?.order) qs.set("order", filter.order);
  if (filter?.page) qs.set("page", String(filter.page));
  if (filter?.pageSize) qs.set("pageSize", String(filter.pageSize));
  for (const attr of filter?.attributes ?? []) {
    if (attr.name && attr.value) qs.append("attr", `${attr.name}:${attr.value}`);
  }
  return qs.toString();
}

// ──────────────────────────── Lifecycle suggestions ────────────────────────────

export type WlmSuggestionKind = "onboarding" | "offboarding";
export type WlmSuggestionStatus = "pending" | "accepted" | "dismissed";

/** Fields the detector (keyword screen + Gemini) pulled off the ticket — all optional. */
export interface WlmSuggestionExtracted {
  name?: string;
  email?: string;
  departmentName?: string;
  jobTitle?: string;
  startDate?: string;
}

/** Camelized `wlm_lifecycle_suggestions` row — one detector hit for a ticket. */
export interface WlmLifecycleSuggestion {
  id: string;
  workItemId: string;
  itemKey: string;
  kind: WlmSuggestionKind;
  confidence: number | null;
  extracted: WlmSuggestionExtracted;
  status: WlmSuggestionStatus;
  runParentItemKey: string | null;
  createdAt: string;
}

export interface WlmLifecycleSuggestionsFilter {
  status?: WlmSuggestionStatus;
  page?: number;
  pageSize?: number;
}

/**
 * GET /workforce/tickets/pack-context response — the agent-gated (tickets.read,
 * not WLM admin) equivalent of getWorkforcePack for the ticket sidebar
 * panels: just enough to render the lifecycle-suggestion banner and feed
 * OnboardingDialog's department picker, without the 403 a non-admin agent
 * would get from getWorkforcePack.
 */
export interface WlmTicketPackContext {
  installed: boolean;
  opsPortalId: string | null;
  departments: { id: string; name: string }[];
}

// ──────────────────────────── Directory / IdP inbound sync ────────────────────────────

/** Camelized `DirectorySyncRunSummary` — the most recent IdP inbound sync run. */
export interface WlmDirectorySyncRun {
  id: string;
  status: string;
  startedAt: string | null;
  finishedAt: string | null;
  created: number;
  updated: number;
}

/** GET /workforce/directory-sync/status response. */
export interface WlmDirectorySyncStatus {
  lastRun: WlmDirectorySyncRun | null;
  pendingSuggestions: number;
}

/**
 * Camelized `wlm_sync_suggestions` row (pending only) — one detector hit
 * from an IdP inbound sync run (e.g. an account disabled upstream).
 * `detail` typically carries `{ email, name, source }`.
 */
export interface WlmSyncSuggestion {
  id: string;
  partyId: string;
  kind: string;
  status: string;
  detail: Record<string, unknown>;
  createdAt: string;
}

/**
 * POST /workforce/directory-sync/suggestions/:id/offboard response. Always
 * 200; `resolved` says whether the suggestion was consumed, and it is NOT
 * always true:
 *
 *  - `lifecycle` + `resolved: true`  — run created, suggestion cleared.
 *  - `lifecycle` + `resolved: false` — run created, but clearing the row
 *    failed; it may still be listed.
 *  - `lifecycleError` + `resolved: false` — the suggestion is still pending.
 *    A retry is safe UNLESS `lifecycleErrorCode` is ALREADY_IN_PROGRESS, which
 *    means a run exists and the row just needs dismissing.
 *
 * The third case is the one worth reading carefully: the suggestion used to be
 * resolved BEFORE the run was attempted, so a failure discarded the only
 * handle on an Entra-detected departure (see
 * server/routes/workforce/directorySyncRoutes.ts).
 */
export interface WlmDirectorySyncOffboardResult {
  ok: boolean;
  lifecycle?: WlmLifecycleResult;
  resolved?: boolean;
  lifecycleError?: string;
  /**
   * Set when the failure was a WorkforceConflictError. ALREADY_IN_PROGRESS is
   * the one a retry can never clear — and it is reachable from this route's
   * own success path, since a run whose suggestion failed to resolve leaves
   * the row pending and every later click lands here.
   */
  lifecycleErrorCode?: string;
}

// ──────────────────────────── Dashboard ────────────────────────────

export interface WlmDashboardDeptHeadcount {
  departmentId: string | null;
  name: string;
  count: number;
}

export interface WlmDashboardInMotion {
  onboarding: number;
  moves: number;
  offboarding: number;
  tasksDue: number;
}

export interface WlmDashboardAssetStatusCount {
  status: string;
  count: number;
}

export interface WlmDashboardSeatUtilization {
  licenseId: string;
  name: string;
  seatsUsed: number;
  seatsTotal: number | null;
}

export interface WlmDashboardLicenseCost {
  monthlyTotal: number;
  licenseCount: number;
}

export interface WlmDashboardLifecycleActivity {
  weekStart: string;
  onboarding: number;
  moves: number;
  offboarding: number;
}

/** GET /workforce/dashboard/summary — server-side SQL rollups for the WLM
 * current-state dashboard (WorkforceDashboardsPage). */
export interface WlmDashboardSummary {
  headcount: number;
  headcountByDepartment: WlmDashboardDeptHeadcount[];
  inMotion: WlmDashboardInMotion;
  assetsByStatus: WlmDashboardAssetStatusCount[];
  seatUtilization: WlmDashboardSeatUtilization[];
  licenseCost: WlmDashboardLicenseCost;
  lifecycleActivity: WlmDashboardLifecycleActivity[];
}

/** Optional scoping for the dashboard summary/trends queries. `departmentId`
 * narrows the rollups that carry a department dimension; `from`/`to` (ISO
 * `YYYY-MM-DD`) bound the time-based aggregates. All optional — omit for the
 * tenant-wide, full-history default. */
export interface WlmDashboardFilters {
  departmentId?: string;
  from?: string;
  to?: string;
}

/** One day's flattened wlm_metrics_snapshots row, as read back by
 * GET /workforce/dashboard/trends for the dashboard's trend charts (Task 5). */
export interface WlmTrendPoint {
  periodDate: string;
  headcount: number;
  onboarding: number;
  moves: number;
  offboarding: number;
  tasksDue: number;
  assetsTotal: number;
  assetsAssigned: number;
  assetsInRepair: number;
  seatsTotal: number;
  seatsUsed: number;
  licenseCount: number;
  licenseMonthlyCost: number;
}

/** GET /workforce/reports/access-holdings — instance-wide "who has what
 * access" (skySupport's own Access Management assignments), joined with
 * person name/department, for the Compliance reports "Access holdings" tab. */
export interface WlmAccessHolding {
  partyId: string | null;
  name: string;
  email: string;
  department: string | null;
  appSlug: string;
  appName: string;
  roleName: string | null;
  roleType: string | null;
  status: string | null;
  assignedAt: string | null;
}

/** GET /workforce/reports/access-history — instance-wide access change
 * ledger (user_role_history), optionally bounded by ?from=&to=, for the
 * Compliance reports "Access change history" tab. */
export interface WlmAccessChange {
  id: string;
  targetEmail: string;
  actor: string | null;
  action: string;
  fieldName: string | null;
  previousValue: string | null;
  newValue: string | null;
  createdAt: string;
}

/** GET /workforce/reports/license-compliance — per-license seat usage +
 * overAllocated flag, for the Compliance reports "License compliance" tab. */
export interface WlmLicenseCompliance {
  licenseId: string;
  name: string;
  vendor: string | null;
  kind: string;
  seatsTotal: number | null;
  seatsUsed: number;
  seatsFree: number | null;
  overAllocated: boolean;
  monthlyCost: number | null;
  renewalDate: string | null;
}

/** GET /workforce/reports/seat-holders — every active seat across every
 * license, for the Compliance reports "License compliance" tab. */
export interface WlmSeatHolder {
  licenseId: string;
  partyName: string;
  partyEmail: string;
  grantedAt: string;
  grantedByEmail: string | null;
}

/** GET /workforce/reports/license-reconciliation — entitled vs allocated vs
 * provisioned per license, with findings. `signal` distinguishes a real
 * reconciliation from an unmapped license or an unreachable connector. */
export type WlmReconSignal = "reconciled" | "none" | "unmapped";
export interface WlmReconFinding {
  kind: "reclaim_candidate" | "unlicensed_access" | "orphaned_seat" | "over_allocated";
  email: string | null;
  name: string | null;
  detail: string;
  // Only set for findings tied to an actual seat (reclaim_candidate); needed
  // to target the revokeSeat mutation. Optional so older/partial fixtures
  // (and any signal that hasn't been re-fetched) still type-check.
  partyId?: string | null;
}
export interface WlmLicenseReconciliation {
  licenseId: string;
  name: string;
  vendor: string;
  kind: string;
  signal: WlmReconSignal;
  seatsTotal: number | null;
  allocated: number;
  provisioned: number | null;
  findings: WlmReconFinding[];
  estimatedMonthlyWaste: number | null;
}

// ── Separation of duties (2e) ──

export type WlmSodSeverity = "low" | "medium" | "high";

/** SoD grant-time enforcement mode (2e). */
export type WlmSodMode = "off" | "warn" | "block";

/** One assignable role for the SoD rule editor's dropdowns (GET /workforce/sod/roles). */
export interface WlmRoleCatalogEntry {
  id: string;
  name: string;
  /** department_role: a role from Departments & Roles, held through a person's job (#900). */
  roleType: "custom_role" | "end_user_role" | "department_role";
}

/** A conflicting-role-pair rule (GET /workforce/sod/rules). */
export interface WlmSodRule {
  id: string;
  name: string | null;
  roleAId: string;
  roleAName: string;
  roleBId: string;
  roleBName: string;
  severity: WlmSodSeverity;
  description: string | null;
  createdAt: string;
}

export interface WlmSodRuleInput {
  roleAId: string;
  roleBId: string;
  name?: string;
  severity: WlmSodSeverity;
  description?: string;
}

/** One current violation — a person holding both roles of a rule (GET /workforce/sod/violations). */
export interface WlmSodViolation {
  ruleId: string;
  ruleName: string | null;
  severity: WlmSodSeverity;
  partyId: string | null;
  name: string;
  email: string;
  department: string | null;
  roleAName: string;
  roleBName: string;
}

// ── Employee self-service (5a) ──

/** GET /workforce/me — the caller's own workforce record (read-only). */
export interface WlmMyWorkspace {
  person: {
    partyId: string;
    name: string;
    email: string;
    jobTitle: string | null;
    department: string | null;
    status: string | null;
    startDate: string | null;
  };
  access: {
    roleName: string | null;
    roleType: string | null;
    status: string | null;
    assignedAt: string | null;
  }[];
  /**
   * Roles across every Skypoint app — skySupport included, once — from the
   * same cross-app read as the admin profile's Access tile and "Current
   * access" card (#1425). My access lists and counts THIS. `access` above is
   * skySupport's Access-Management view of the same grant; My access does not
   * render it (it double-listed skySupport), only the manager hub does.
   */
  appAccess: { appSlug: string; displayName: string; roles: string[] }[];
  assets: {
    id: string;
    name: string;
    assetTypeName: string | null;
    status: string;
    assignedAt: string | null;
  }[];
  licenses: { licenseId: string; name: string; grantedAt: string }[];
}

/** One employee onboarding checklist task (5c) — distinct from the pack-template
 * WlmOnboardingTask; this is a per-person instance with completion state. */
export interface WlmMyOnboardingTask {
  id: string;
  title: string;
  description: string | null;
  category: string;
  status: "pending" | "done";
  sortOrder: number;
  dueAt: string | null;
  completedAt: string | null;
}

/** Admin create/edit shapes for a person's onboarding tasks (5c depth). */
export interface WlmPersonOnboardingInput {
  title: string;
  description?: string | null;
  category?: string;
  dueAt?: string | null;
}
export interface WlmPersonOnboardingPatch {
  title?: string;
  description?: string | null;
  category?: string;
  status?: "pending" | "done";
  dueAt?: string | null;
}

/** GET /workforce/me/onboarding (5c) — the caller's checklist + progress. */
export interface WlmMyOnboarding {
  total: number;
  done: number;
  tasks: WlmMyOnboardingTask[];
}

/** One direct report in the manager hub (5d). */
export interface WlmTeamMember {
  partyId: string;
  name: string;
  email: string;
  jobTitle: string | null;
  department: string | null;
  status: string | null;
  onboarding: { done: number; total: number };
  assetCount: number;
  accessCount: number;
}

/** GET /workforce/me/team (5d) — the caller's direct reports. */
export interface WlmMyTeam {
  reports: WlmTeamMember[];
}

/** GET /workforce/me/team/members/:partyId (5d) — a report's workspace + onboarding. */
export interface WlmTeamMemberWorkspace extends WlmMyWorkspace {
  onboarding: WlmMyOnboarding | null;
}

/** POST /workforce/me/requests body (5b) — the 2c input minus subjectEmail (server forces it to the caller). */
export type WlmSelfAccessRequestInput =
  | { itemType: "app_access"; appSlug: string; roleKey: string; justification?: string }
  | { itemType: "license_seat"; licenseId: string; justification?: string };

// ── Access request catalog (2c) ──

export type WlmAccessRequestItemType = "app_access" | "license_seat";
export type WlmAccessRequestStatus = "pending" | "fulfilled" | "failed" | "rejected";

/** GET /workforce/access-requests/catalog — what a requester can pick from. */
export interface WlmRequestCatalog {
  apps: WlmGrantableApp[];
  licenses: { id: string; name: string; vendor: string | null; seatsFree: number }[];
}

/** One access request (GET /workforce/access-requests). */
export interface WlmAccessRequest {
  id: string;
  requesterEmail: string | null;
  subjectEmail: string;
  subjectName: string | null;
  itemType: WlmAccessRequestItemType;
  itemRef: { appSlug?: string; roleKey?: string; licenseId?: string };
  itemLabel: string | null;
  justification: string | null;
  status: WlmAccessRequestStatus;
  decidedByEmail: string | null;
  decidedAt: string | null;
  fulfillmentNote: string | null;
  createdAt: string;
  /** Pending rows only: may THIS caller decide it (#1418)? */
  canDecide?: boolean;
  blockedReason?: string;
}

/** POST body — discriminated on itemType, mirroring accessRequestBodySchema. */
export type WlmAccessRequestInput =
  | {
      itemType: "app_access";
      appSlug: string;
      roleKey: string;
      subjectEmail: string;
      justification?: string;
    }
  | { itemType: "license_seat"; licenseId: string; subjectEmail: string; justification?: string };

/** GET /workforce/certifications row — one access-certification campaign
 * (Wave 4 "who has access" attestation sweep), for the Access certification
 * list page. */
export interface WlmCertCampaign {
  id: string;
  instanceId: string;
  name: string;
  status: "open" | "completed";
  totalItems: number;
  attestedCount: number;
  revokedCount: number;
  createdByEmail: string;
  createdAt: string;
  completedAt: string | null;
}

/** One line item inside a certification campaign — a single person/app/role
 * grant awaiting (or carrying) a reviewer decision. Consumed by the Task 5
 * detail/review view. */
export interface WlmCertItem {
  id: string;
  campaignId: string;
  partyId: string;
  name: string;
  email: string;
  department: string | null;
  appSlug: string;
  appName: string;
  roleName: string | null;
  roleType: string | null;
  decision: "pending" | "attested" | "revoked";
  decidedByEmail: string | null;
  decidedAt: string | null;
  revokeTicketKey: string | null;
}

/** GET /workforce/certifications/:id response shape — the campaign plus its
 * full item list, for the Task 5 review view. */
export interface WlmCertCampaignDetail {
  campaign: WlmCertCampaign;
  items: WlmCertItem[];
}

// ── Org chart ──
export interface OrgChartNode {
  partyId: string;
  name: string;
  title: string | null;
  department: string | null;
  email: string | null;
  /** Active-employment fields for the detail panel; optional so lightweight
   * fixtures/search rows without them still type-check. */
  employmentType?: string | null;
  startDate?: string | null;
  managerPartyId: string | null;
  directReportCount: number;
  isActive: boolean;
  entraManagerSyncStatus: string | null;
}
export interface OrgFocusResponse {
  ancestors: OrgChartNode[];
  focus: OrgChartNode;
  children: OrgChartNode[];
}
export interface OrgChartFilters {
  departmentIds?: string[];
  activeOnly?: boolean;
  locationIds?: string[];
  groupIds?: string[];
}

// ──────────────────────────── Tag helpers ────────────────────────────

const PACK_STATE_TAG = { type: "WorkforcePack" as const, id: "STATE" };
const ASSET_LIST_TAG = { type: "WorkforceAsset" as const, id: "LIST" };
const ASSET_GRAPH_TAG = { type: "WorkforceAsset" as const, id: "GRAPH" };
const PERSON_LIST_TAG = { type: "WorkforcePerson" as const, id: "LIST" };
const LOCATIONS_TAG = { type: "WorkforceLocation" as const, id: "LIST" };
const ticketLinksTag = (workItemId: string) => ({
  type: "WorkforceAsset" as const,
  id: `links-${workItemId}`,
});

// Lifecycle history feeds (LifecyclePage's Activity/Tickets tabs) — invalidated
// by every mutation that changes what those feeds show (runs started, moves
// executed, suggestions dismissed) so the page refreshes instead of needing a
// hard reload.
const RUN_LIST_TAG = { type: "WorkforceLifecycleRun" as const, id: "LIST" };
const AUDIT_LIST_TAG = { type: "WorkforceAudit" as const, id: "LIST" };

// Wave 2 approval gate — the Pending Approvals surface (LifecyclePage) is
// driven by a plain useGetLifecycleApprovalsQuery, invalidated by
// approve/reject so a decided run drops off the list without a reload.
const APPROVAL_LIST_TAG = { type: "WorkforceApproval" as const, id: "LIST" };

const SUGGESTION_LIST_TAG = { type: "WorkforceSuggestion" as const, id: "LIST" };
// Keyed by the ticket's workItemId — matches getTicketSuggestion's query arg,
// so dismiss (which only knows workItemId) can invalidate it directly.
const ticketSuggestionTag = (workItemId: string) => ({
  type: "WorkforceSuggestion" as const,
  id: `ticket-${workItemId}`,
});
// Keyed by the suggestion's own id — provided alongside the ticket tag when a
// suggestion exists, so startOnboarding/startOffboarding (which only know
// suggestionId, not the ticket's workItemId) can invalidate it on accept.
const suggestionIdTag = (suggestionId: string) => ({
  type: "WorkforceSuggestion" as const,
  id: suggestionId,
});

const LICENSE_LIST_TAG = { type: "WorkforceLicense" as const, id: "LIST" };
const ACCESS_GRANT_LIST_TAG = { type: "WorkforceAccessGrant" as const, id: "LIST" };
const APP_APPROVER_TAG = { type: "WorkforceAppApprover" as const, id: "LIST" };
const TICKET_TEMPLATE_TAG = { type: "WorkforceTicketTemplate" as const, id: "LIST" };
const DIRECTORY_SYNC_TAG = { type: "WorkforceDirectorySync" as const, id: "STATE" };
/** Builds the query-string params for a dashboard request, dropping blank/
 * undefined filters so an unset control never reaches the server as "". */
function dashboardParams(
  arg?: WlmDashboardFilters | null,
  days?: number,
): Record<string, string | number> | undefined {
  const params: Record<string, string | number> = {};
  if (days) params.days = days;
  if (arg?.departmentId) params.departmentId = arg.departmentId;
  if (arg?.from) params.from = arg.from;
  if (arg?.to) params.to = arg.to;
  return Object.keys(params).length > 0 ? params : undefined;
}

/** Stable per-filter cache-tag id so each department/date/`days` combination
 * is tagged (and therefore cached) separately under the shared
 * "WorkforceDashboard" type. */
function dashboardCacheId(arg?: WlmDashboardFilters | null, days?: number): string {
  return [days ?? "", arg?.departmentId ?? "", arg?.from ?? "", arg?.to ?? ""].join("|");
}
const REPORT_TAG = { type: "WorkforceReport" as const, id: "LIST" };
const CERT_CAMPAIGN_LIST_TAG = { type: "WorkforceCertification" as const, id: "LIST" };
const certCampaignIdTag = (id: string) => ({ type: "WorkforceCertification" as const, id });
const SOD_RULE_LIST_TAG = { type: "WorkforceSodRule" as const, id: "LIST" };
const ACCESS_REQUEST_LIST_TAG = { type: "WorkforceAccessRequest" as const, id: "LIST" };
const licenseIdTag = (licenseId: string) => ({
  type: "WorkforceLicense" as const,
  id: licenseId,
});

const ORG_TAG = "OrgChart" as const;

/**
 * A reporting-line change is visible in four places: the org chart, every
 * person profile (the moved person's "Reports to" plus the old and new
 * manager's direct-report counts), the People list, and the manager hub's
 * team view. Both manager mutations hit the same endpoint, so they refresh the
 * same set — otherwise a change made on one surface stays stale on the others
 * until a hard refresh.
 */
const REPORTING_LINE_TAGS = [ORG_TAG, "WorkforcePerson", "WorkforceMyTeam"] as const;

/** Builds the query-string params for an org-chart request, encoding array
 * filters as comma-joined values and dropping any that are empty/unset. */
function orgFilterParams(f?: OrgChartFilters): Record<string, string> {
  const p: Record<string, string> = {};
  if (f?.departmentIds?.length) p.departmentIds = f.departmentIds.join(",");
  if (f?.locationIds?.length) p.locationIds = f.locationIds.join(",");
  if (f?.groupIds?.length) p.groupIds = f.groupIds.join(",");
  if (f?.activeOnly) p.activeOnly = "true";
  return p;
}

// ──────────────────────────── Endpoints ────────────────────────────

export const workforceApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    // ── Pack ──
    getWorkforcePack: builder.query<WorkforcePackPayload, void>({
      query: () => ({ url: "/workforce/pack" }),
      providesTags: [PACK_STATE_TAG],
    }),

    installPack: builder.mutation<WorkforcePackPayload, { sectorId: WlmSectorId }>({
      query: (body) => ({ url: "/workforce/pack/install", method: "POST", body }),
      invalidatesTags: [PACK_STATE_TAG, ASSET_LIST_TAG],
    }),

    resetPack: builder.mutation<WorkforcePackPayload, void>({
      query: () => ({ url: "/workforce/pack/reset", method: "POST" }),
      invalidatesTags: [PACK_STATE_TAG, ASSET_LIST_TAG],
    }),

    updateWorkforceSettings: builder.mutation<WorkforcePackPayload, WlmWorkforceSettingsInput>({
      query: (body) => ({ url: "/workforce/settings", method: "PATCH", body }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    // ── Locations ──
    //
    // The same `wlm_locations` rows skyEmployee's Users & organisation screen
    // writes, and the ones directory sync creates from people's office
    // locations. A "team" is a (location, department) pair, so these are half
    // the vocabulary audiences and Manager scope are built from.
    getWlmLocations: builder.query<WlmLocation[], { includeArchived?: boolean } | void>({
      query: (args) => ({
        url: "/workforce/locations",
        params: args?.includeArchived ? { includeArchived: "true" } : {},
      }),
      providesTags: [LOCATIONS_TAG],
    }),

    createWlmLocation: builder.mutation<WlmLocation, { name: string }>({
      query: (body) => ({ url: "/workforce/locations", method: "POST", body }),
      // Placing someone reads the location list, and a person's profile shows
      // the name, so both have to refresh when a location changes.
      invalidatesTags: [LOCATIONS_TAG],
    }),

    updateWlmLocation: builder.mutation<
      WlmLocation,
      { id: string; body: { name?: string; status?: "active" | "archived" } }
    >({
      query: ({ id, body }) => ({ url: `/workforce/locations/${id}`, method: "PATCH", body }),
      invalidatesTags: [LOCATIONS_TAG, "WorkforcePerson"],
    }),

    setPersonLocation: builder.mutation<void, { agentId: string; locationId: string }>({
      query: ({ agentId, locationId }) => ({
        url: `/workforce/people/${agentId}/location`,
        method: "PUT",
        body: { locationId },
      }),
      // The profile shows the location, and the people list shows it too.
      invalidatesTags: ["WorkforcePerson", PERSON_LIST_TAG],
    }),

    // ── Departments ──
    createDepartment: builder.mutation<Omit<WlmDepartment, "roles">, WlmDepartmentInput>({
      query: (body) => ({ url: "/workforce/departments", method: "POST", body }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    updateDepartment: builder.mutation<
      Omit<WlmDepartment, "roles">,
      { id: string; body: WlmDepartmentPatch }
    >({
      query: ({ id, body }) => ({ url: `/workforce/departments/${id}`, method: "PATCH", body }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    deleteDepartment: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({ url: `/workforce/departments/${id}`, method: "DELETE" }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    addDepartmentRole: builder.mutation<WlmDepartmentRole, { departmentId: string; name: string }>({
      query: ({ departmentId, name }) => ({
        url: `/workforce/departments/${departmentId}/roles`,
        method: "POST",
        body: { name },
      }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    deleteDepartmentRole: builder.mutation<
      { success: boolean; message: string },
      { departmentId: string; roleId: string }
    >({
      query: ({ departmentId, roleId }) => ({
        url: `/workforce/departments/${departmentId}/roles/${roleId}`,
        method: "DELETE",
      }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    // ── Department onboarding task templates ──
    addOnboardingTask: builder.mutation<
      WlmOnboardingTask,
      { departmentId: string; body: WlmOnboardingTaskInput }
    >({
      query: ({ departmentId, body }) => ({
        url: `/workforce/departments/${departmentId}/onboarding-tasks`,
        method: "POST",
        body,
      }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    updateOnboardingTask: builder.mutation<
      WlmOnboardingTask,
      { departmentId: string; taskId: string; body: WlmOnboardingTaskPatch }
    >({
      query: ({ departmentId, taskId, body }) => ({
        url: `/workforce/departments/${departmentId}/onboarding-tasks/${taskId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    deleteOnboardingTask: builder.mutation<
      { success: boolean; message: string },
      { departmentId: string; taskId: string }
    >({
      query: ({ departmentId, taskId }) => ({
        url: `/workforce/departments/${departmentId}/onboarding-tasks/${taskId}`,
        method: "DELETE",
      }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    // ── Ticket templates ──
    getTicketTemplates: builder.query<WlmTicketTemplate[], void>({
      query: () => "/workforce/ticket-templates",
      transformResponse: (res: { data: WlmTicketTemplate[] }) => res.data,
      providesTags: [TICKET_TEMPLATE_TAG],
    }),

    createTicketTemplate: builder.mutation<WlmTicketTemplate, WlmTicketTemplateInput>({
      query: (body) => ({ url: "/workforce/ticket-templates", method: "POST", body }),
      invalidatesTags: [TICKET_TEMPLATE_TAG],
    }),

    updateTicketTemplate: builder.mutation<
      WlmTicketTemplate,
      { id: string; body: WlmTicketTemplatePatch }
    >({
      query: ({ id, body }) => ({
        url: `/workforce/ticket-templates/${id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [TICKET_TEMPLATE_TAG],
    }),

    deleteTicketTemplate: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({ url: `/workforce/ticket-templates/${id}`, method: "DELETE" }),
      invalidatesTags: [TICKET_TEMPLATE_TAG],
    }),

    // Department onboarding checklist = its attached ticket templates.
    attachDeptTicketTemplate: builder.mutation<
      { success: boolean },
      { departmentId: string; templateId: string }
    >({
      query: ({ departmentId, templateId }) => ({
        url: `/workforce/departments/${departmentId}/ticket-templates`,
        method: "POST",
        body: { templateId },
      }),
      invalidatesTags: [TICKET_TEMPLATE_TAG],
    }),

    detachDeptTicketTemplate: builder.mutation<
      { success: boolean; message: string },
      { departmentId: string; templateId: string }
    >({
      query: ({ departmentId, templateId }) => ({
        url: `/workforce/departments/${departmentId}/ticket-templates/${templateId}`,
        method: "DELETE",
      }),
      invalidatesTags: [TICKET_TEMPLATE_TAG],
    }),

    // ── Asset types ──
    createAssetType: builder.mutation<Omit<WlmAssetType, "attributes">, { name: string }>({
      query: (body) => ({ url: "/workforce/asset-types", method: "POST", body }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    updateAssetType: builder.mutation<
      Omit<WlmAssetType, "attributes">,
      { id: string; name: string }
    >({
      query: ({ id, name }) => ({
        url: `/workforce/asset-types/${id}`,
        method: "PATCH",
        body: { name },
      }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    deleteAssetType: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({ url: `/workforce/asset-types/${id}`, method: "DELETE" }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    addAssetTypeAttribute: builder.mutation<
      WlmAssetTypeAttribute,
      {
        assetTypeId: string;
        name: string;
        attributeType?: WlmAttributeType;
        referenceAssetTypeId?: string | null;
      }
    >({
      query: ({ assetTypeId, name, attributeType, referenceAssetTypeId }) => ({
        url: `/workforce/asset-types/${assetTypeId}/attributes`,
        method: "POST",
        body: { name, attributeType, referenceAssetTypeId },
      }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    updateAssetTypeAttribute: builder.mutation<
      WlmAssetTypeAttribute,
      {
        assetTypeId: string;
        attrId: string;
        body: {
          name?: string;
          attributeType?: WlmAttributeType;
          referenceAssetTypeId?: string | null;
        };
      }
    >({
      query: ({ assetTypeId, attrId, body }) => ({
        url: `/workforce/asset-types/${assetTypeId}/attributes/${attrId}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    deleteAssetTypeAttribute: builder.mutation<
      { success: boolean; message: string },
      { assetTypeId: string; attrId: string }
    >({
      query: ({ assetTypeId, attrId }) => ({
        url: `/workforce/asset-types/${assetTypeId}/attributes/${attrId}`,
        method: "DELETE",
      }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    // FormData body — fetchBaseQuery leaves Content-Type alone so the
    // browser sets the multipart boundary itself.
    uploadAssetTypeImage: builder.mutation<
      Omit<WlmAssetType, "attributes">,
      { id: string; file: File }
    >({
      query: ({ id, file }) => {
        const body = new FormData();
        body.append("file", file);
        return { url: `/workforce/asset-types/${id}/image`, method: "POST", body };
      },
      invalidatesTags: [PACK_STATE_TAG],
    }),

    deleteAssetTypeImage: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({ url: `/workforce/asset-types/${id}/image`, method: "DELETE" }),
      invalidatesTags: [PACK_STATE_TAG],
    }),

    // ── Assets ──
    getAssets: builder.query<{ data: WlmAsset[]; total: number }, WlmAssetListFilter | void>({
      query: (filter) => {
        const qs = buildAssetListQueryString(filter ?? undefined);
        return { url: qs ? `/workforce/assets?${qs}` : "/workforce/assets" };
      },
      providesTags: [ASSET_LIST_TAG],
    }),

    // Per-id tag (not the list tag) — the detail drawer opens on one asset
    // at a time via `?asset=<id>`, and status-transition mutations below
    // additionally invalidate `{WorkforceAsset, id}` so the open drawer
    // refetches when its own asset changes.
    getAssetDetail: builder.query<WlmAssetDetail, string>({
      query: (assetId) => ({ url: `/workforce/assets/${assetId}` }),
      providesTags: (_result, _error, assetId) => [{ type: "WorkforceAsset", id: assetId }],
    }),

    // Read-only exploration, but tagged with the shared GRAPH marker so any
    // asset mutation (create/update/delete/assign/unassign) invalidates it —
    // otherwise a graph explorer opened before a reference-attribute edit
    // would show stale edges/nodes until a hard reload. Recentering (new
    // `assetId`) still refetches on its own via RTK Query's normal
    // refetch-on-arg-change.
    getAssetGraph: builder.query<WlmAssetGraph, { assetId: string; depth?: number }>({
      query: ({ assetId, depth }) => ({
        url: `/workforce/assets/${assetId}/graph`,
        params: depth ? { depth } : undefined,
      }),
      providesTags: [ASSET_GRAPH_TAG],
    }),

    createAsset: builder.mutation<WlmAsset, WlmAssetInput>({
      query: (body) => ({ url: "/workforce/assets", method: "POST", body }),
      invalidatesTags: [ASSET_LIST_TAG, PERSON_LIST_TAG, ASSET_GRAPH_TAG],
    }),

    bulkImportAssets: builder.mutation<{ created: number }, WlmAssetBulkImportInput>({
      query: (body) => ({ url: "/workforce/assets/bulk", method: "POST", body }),
      invalidatesTags: [ASSET_LIST_TAG, PACK_STATE_TAG, ASSET_GRAPH_TAG],
    }),

    updateAsset: builder.mutation<WlmAsset, { id: string; body: WlmAssetPatch }>({
      query: ({ id, body }) => ({ url: `/workforce/assets/${id}`, method: "PATCH", body }),
      invalidatesTags: (_result, _error, arg) => [
        ASSET_LIST_TAG,
        PERSON_LIST_TAG,
        ASSET_GRAPH_TAG,
        // #1429: the drawer's timeline reads the audit list, so an asset
        // action has to invalidate it or the entry it just wrote stays
        // invisible until a reload.
        AUDIT_LIST_TAG,
        { type: "WorkforceAsset", id: arg.id },
      ],
    }),

    deleteAsset: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({ url: `/workforce/assets/${id}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [
        ASSET_LIST_TAG,
        PERSON_LIST_TAG,
        ASSET_GRAPH_TAG,
        // #1429: the drawer's timeline reads the audit list, so an asset
        // action has to invalidate it or the entry it just wrote stays
        // invisible until a reload.
        AUDIT_LIST_TAG,
        { type: "WorkforceAsset", id },
      ],
    }),

    // FormData body — see uploadAssetTypeImage. `attribute` names which
    // image-typed attribute of the asset's type this file fills; the blob
    // name lands in the asset's attributes jsonb under that key.
    // Invalidates the list (row tiles) and the per-id tag (open drawer).
    uploadAssetAttributeImage: builder.mutation<
      WlmAsset,
      { id: string; attribute: string; file: File }
    >({
      query: ({ id, attribute, file }) => {
        const body = new FormData();
        body.append("file", file);
        body.append("attribute", attribute);
        return { url: `/workforce/assets/${id}/attribute-image`, method: "POST", body };
      },
      invalidatesTags: (_result, _error, arg) => [
        ASSET_LIST_TAG,
        { type: "WorkforceAsset", id: arg.id },
      ],
    }),

    deleteAssetAttributeImage: builder.mutation<
      { success: boolean; message: string },
      { id: string; attribute: string }
    >({
      query: ({ id, attribute }) => ({
        url: `/workforce/assets/${id}/attribute-image?attribute=${encodeURIComponent(attribute)}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        ASSET_LIST_TAG,
        { type: "WorkforceAsset", id: arg.id },
      ],
    }),

    assignAsset: builder.mutation<
      WlmAsset,
      { id: string; agentId: string; previousAgentId?: string; confirmOffboarding?: boolean }
    >({
      query: ({ id, agentId, confirmOffboarding }) => ({
        url: `/workforce/assets/${id}/assign`,
        method: "POST",
        body: { agentId, ...(confirmOffboarding ? { confirmOffboarding } : {}) },
      }),
      // previousAgentId is optional caller-supplied metadata (the asset's
      // holder before this reassignment, when known) purely for cache
      // invalidation — without it, the previous holder's profile
      // (Assets held / asset count) stays stale until a hard reload.
      invalidatesTags: (_result, _error, arg) => [
        ASSET_LIST_TAG,
        PERSON_LIST_TAG,
        ASSET_GRAPH_TAG,
        // #1429: the drawer's timeline reads the audit list, so an asset
        // action has to invalidate it or the entry it just wrote stays
        // invisible until a reload.
        AUDIT_LIST_TAG,
        { type: "WorkforcePerson", id: arg.agentId },
        { type: "WorkforceAsset", id: arg.id },
        ...(arg.previousAgentId
          ? [{ type: "WorkforcePerson" as const, id: arg.previousAgentId }]
          : []),
      ],
    }),

    // #1429: completing a repair has its own endpoint. It cannot be a status
    // PATCH (updateAsset refuses status:'assigned'), and expressing it as a
    // re-assign logged "assigned asset X to Y" — a handover that never
    // happened.
    markAssetFixed: builder.mutation<WlmAsset, { id: string; agentId?: string }>({
      query: ({ id }) => ({ url: `/workforce/assets/${id}/mark-fixed`, method: "POST" }),
      invalidatesTags: (_result, _error, arg) => [
        ASSET_LIST_TAG,
        PERSON_LIST_TAG,
        ASSET_GRAPH_TAG,
        AUDIT_LIST_TAG,
        { type: "WorkforceAsset", id: arg.id },
        ...(arg.agentId ? [{ type: "WorkforcePerson" as const, id: arg.agentId }] : []),
      ],
    }),

    unassignAsset: builder.mutation<WlmAsset, { id: string; agentId?: string }>({
      query: ({ id }) => ({ url: `/workforce/assets/${id}/unassign`, method: "POST" }),
      // agentId is optional metadata from the caller (the asset's current holder)
      // so the affected person's profile cache can be invalidated when known.
      invalidatesTags: (_result, _error, arg) => [
        ASSET_LIST_TAG,
        PERSON_LIST_TAG,
        ASSET_GRAPH_TAG,
        // #1429: the drawer's timeline reads the audit list, so an asset
        // action has to invalidate it or the entry it just wrote stays
        // invisible until a reload.
        AUDIT_LIST_TAG,
        { type: "WorkforceAsset", id: arg.id },
        ...(arg.agentId ? [{ type: "WorkforcePerson" as const, id: arg.agentId }] : []),
      ],
    }),

    // ── People ──
    getPeople: builder.query<{ data: WlmPerson[]; total: number }, WlmPeopleFilter | void>({
      query: (params) => ({ url: "/workforce/people", params: params ?? undefined }),
      providesTags: [PERSON_LIST_TAG],
    }),

    /**
     * Everyone in the workforce, for pickers that filter in the browser.
     * The people endpoint pages at most 200 at a time, and pickers used to
     * load only the first page, so anyone past it — late in the alphabet —
     * could not be found or picked (#1520). Loads page 1, then the rest at
     * once.
     */
    getAllPeople: builder.query<WlmPerson[], void>({
      async queryFn(_arg, _api, _extra, baseQuery) {
        const page = (n: number) =>
          baseQuery({
            url: "/workforce/people",
            params: { page: n, pageSize: PEOPLE_PAGE_MAX },
          });
        const first = await page(1);
        if (first.error) return { error: first.error };
        const { data, total } = first.data as { data: WlmPerson[]; total: number };
        const pages = Math.ceil(total / PEOPLE_PAGE_MAX);
        const rest = await Promise.all(
          Array.from({ length: Math.max(0, pages - 1) }, (_, i) => page(i + 2)),
        );
        const failed = rest.find((r) => r.error);
        if (failed?.error) return { error: failed.error };
        return {
          data: [...data, ...rest.flatMap((r) => (r.data as { data: WlmPerson[] }).data)],
        };
      },
      providesTags: [PERSON_LIST_TAG],
    }),

    getPersonProfile: builder.query<
      WlmPersonProfile,
      { agentId: string; from?: string; to?: string }
    >({
      query: ({ agentId, from, to }) => ({
        url: `/workforce/people/${agentId}/profile`,
        params: from || to ? { from, to } : undefined,
      }),
      providesTags: (_result, _error, arg) => [{ type: "WorkforcePerson", id: arg.agentId }],
    }),

    // Cross-app access for one person (all registered Skypoint apps). Separate
    // from the profile query because its S2S fan-out is slower.
    getPersonAppAccess: builder.query<WlmPersonAppAccess[], string>({
      query: (agentId) => `/workforce/people/${agentId}/app-access`,
      transformResponse: (res: { data: WlmPersonAppAccess[] }) => res.data,
      providesTags: (_result, _error, agentId) => [{ type: "WorkforcePerson", id: agentId }],
    }),

    updatePersonDetails: builder.mutation<
      WlmPersonDetails,
      { agentId: string; body: WlmPersonDetailsPatch }
    >({
      query: ({ agentId, body }) => ({
        url: `/workforce/people/${agentId}/details`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [{ type: "WorkforcePerson", id: arg.agentId }],
    }),

    // PUT /workforce/people/:agentId/manager (5d) — set/clear the reporting line.
    setPersonManager: builder.mutation<unknown, { agentId: string; managerPartyId: string | null }>(
      {
        query: ({ agentId, managerPartyId }) => ({
          url: `/workforce/people/${agentId}/manager`,
          method: "PUT",
          body: { managerPartyId },
        }),
        invalidatesTags: [...REPORTING_LINE_TAGS],
      },
    ),

    // ── Admin per-person onboarding task management (5c depth) ──
    getPersonOnboarding: builder.query<WlmMyOnboardingTask[], string>({
      query: (agentId) => ({ url: `/workforce/people/${agentId}/onboarding` }),
      transformResponse: (res: { data: WlmMyOnboardingTask[] }) => res.data,
      providesTags: (_r, _e, agentId) => [{ type: "WorkforcePerson", id: `onb-${agentId}` }],
    }),
    addPersonOnboardingTask: builder.mutation<
      WlmMyOnboardingTask,
      { agentId: string; body: WlmPersonOnboardingInput }
    >({
      query: ({ agentId, body }) => ({
        url: `/workforce/people/${agentId}/onboarding`,
        method: "POST",
        body,
      }),
      transformResponse: (res: { data: WlmMyOnboardingTask }) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: "WorkforcePerson", id: `onb-${arg.agentId}` }],
    }),
    updatePersonOnboardingTask: builder.mutation<
      WlmMyOnboardingTask,
      { agentId: string; taskId: string; body: WlmPersonOnboardingPatch }
    >({
      query: ({ agentId, taskId, body }) => ({
        url: `/workforce/people/${agentId}/onboarding/${taskId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (res: { data: WlmMyOnboardingTask }) => res.data,
      invalidatesTags: (_r, _e, arg) => [{ type: "WorkforcePerson", id: `onb-${arg.agentId}` }],
    }),
    deletePersonOnboardingTask: builder.mutation<
      { ok: boolean },
      { agentId: string; taskId: string }
    >({
      query: ({ agentId, taskId }) => ({
        url: `/workforce/people/${agentId}/onboarding/${taskId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_r, _e, arg) => [{ type: "WorkforcePerson", id: `onb-${arg.agentId}` }],
    }),

    // POST /workforce/people/:agentId/employments — direct, ticket-free path
    // to place an existing agent into a department. Invalidates the person's
    // profile, the roster list (departments summary changes), and the pack
    // state (department member counts).
    addEmployment: builder.mutation<
      // `access` is null when the grant rules could not be applied at all.
      WlmEmployment & { access?: WlmGrantOutcome | null },
      { agentId: string; body: WlmAddEmploymentInput }
    >({
      query: ({ agentId, body }) => ({
        url: `/workforce/people/${agentId}/employments`,
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WorkforcePerson", id: arg.agentId },
        PERSON_LIST_TAG,
        PACK_STATE_TAG,
        // #1418: grants held for approval surface in Lifecycle › Approvals.
        APPROVAL_LIST_TAG,
      ],
    }),

    // POST /workforce/people/sync — explicit "Sync now" trigger for
    // materializing/refreshing parties from agents (also runs implicitly at
    // the top of every getPeople call — see workforcePartyService). Refetches
    // the roster (party/department fields) and the pack (department member
    // counts) since both can change from a sync.
    syncPeople: builder.mutation<{ synced: number }, void>({
      query: () => ({ url: "/workforce/people/sync", method: "POST" }),
      // Also refresh the directory-sync status so the "Last synced" chip
      // updates live after a manual sync (the sync now records a run row).
      invalidatesTags: [PERSON_LIST_TAG, PACK_STATE_TAG, DIRECTORY_SYNC_TAG],
    }),

    // ── Lifecycle ──
    startOnboarding: builder.mutation<WlmLifecycleResult, WlmOnboardingInput>({
      query: (body) => ({ url: "/workforce/lifecycle/onboarding", method: "POST", body }),
      // Onboarding creates a party + employment — member counts (pack) and
      // the people list must refetch or the new hire is invisible until a
      // hard reload. When started from a suggestion, the server marks it
      // accepted — refetch the suggestions list + the suggestion itself.
      // Also invalidates the lifecycle history feeds (Activity/Tickets tabs)
      // so a run started from the page header shows up without a reload.
      invalidatesTags: (_result, _error, arg) => [
        PACK_STATE_TAG,
        PERSON_LIST_TAG,
        SUGGESTION_LIST_TAG,
        RUN_LIST_TAG,
        AUDIT_LIST_TAG,
        ...(arg.suggestionId ? [suggestionIdTag(arg.suggestionId)] : []),
      ],
    }),

    // Read-only dry-run preview of the birthright-access grants onboarding
    // would apply — the wizard fires this (via the lazy trigger) once Step 1
    // (person + department) completes, before the run is actually started.
    previewOnboardingGrants: builder.query<
      WlmOnboardingGrantPreview,
      { email: string; name: string; departmentId: string; jobTitle: string | null }
    >({
      query: (body) => ({
        url: "/workforce/lifecycle/onboarding/preview-grants",
        method: "POST",
        body,
      }),
    }),

    startOffboarding: builder.mutation<
      WlmLifecycleResult,
      { agentId: string; employmentId?: string; suggestionId?: string }
    >({
      query: (body) => ({ url: "/workforce/lifecycle/offboarding", method: "POST", body }),
      invalidatesTags: (_result, _error, arg) => [
        { type: "WorkforcePerson", id: arg.agentId },
        PACK_STATE_TAG,
        PERSON_LIST_TAG,
        SUGGESTION_LIST_TAG,
        RUN_LIST_TAG,
        AUDIT_LIST_TAG,
        ...(arg.suggestionId ? [suggestionIdTag(arg.suggestionId)] : []),
      ],
    }),

    // Mover flow: movePreview is a read-only diff (mutation because it's
    // POST + explicitly triggered, not cached); executeMove performs the
    // department change + grant/revoke ticket bundle. agentId rides along
    // for cache invalidation only (the open profile page provides
    // {WorkforcePerson, id: agentId}) — it is stripped from the request body.
    movePreview: builder.mutation<WlmMovePreviewResult, WlmMoveInput>({
      query: (body) => ({ url: "/workforce/lifecycle/move-preview", method: "POST", body }),
    }),

    executeMove: builder.mutation<WlmMoveResult, WlmMoveInput & { agentId?: string }>({
      query: ({ agentId: _agentId, ...body }) => ({
        url: "/workforce/lifecycle/move",
        method: "POST",
        body,
      }),
      invalidatesTags: (_result, _error, arg) => [
        PERSON_LIST_TAG,
        PACK_STATE_TAG,
        RUN_LIST_TAG,
        AUDIT_LIST_TAG,
        // #1418: with Require approval on, the move's grants wait there.
        APPROVAL_LIST_TAG,
        ...(arg.agentId ? [{ type: "WorkforcePerson" as const, id: arg.agentId }] : []),
      ],
    }),

    // Tagged so LifecyclePage's Tickets tab can drive itself with a plain
    // (non-lazy) useGetLifecycleRunsQuery and refresh automatically when a
    // run starts, instead of needing a lazy trigger + a manual refetch signal.
    getLifecycleRuns: builder.query<
      { data: WlmLifecycleRun[]; total: number },
      WlmLifecycleRunsFilter | void
    >({
      query: (params) => ({ url: "/workforce/lifecycle/runs", params: params ?? undefined }),
      providesTags: [RUN_LIST_TAG],
    }),

    // ── Approval gate (Wave 2) ──
    // Tagged so the Pending Approvals surface can drive itself with a plain
    // (non-lazy) useGetLifecycleApprovalsQuery — see getLifecycleRuns above.
    getLifecycleApprovals: builder.query<{ approvals: WlmLifecycleApproval[] }, void>({
      query: () => ({ url: "/workforce/lifecycle/approvals" }),
      providesTags: [APPROVAL_LIST_TAG],
    }),

    // Runs the run's deferred (pending) steps and resolves their tickets on
    // success — invalidates the approvals list (row clears) plus the run
    // list/audit feed (progress + provisioning now visible there too).
    approveLifecycleRun: builder.mutation<{ outcomes: unknown[] }, string>({
      query: (parentItemKey) => ({
        url: `/workforce/lifecycle/runs/${parentItemKey}/approve`,
        method: "POST",
      }),
      invalidatesTags: [APPROVAL_LIST_TAG, RUN_LIST_TAG, AUDIT_LIST_TAG, PERSON_LIST_TAG],
    }),

    // Cancels the run's deferred (pending) steps; child tickets stay open
    // for manual handling. `note` is an optional rejection reason.
    rejectLifecycleRun: builder.mutation<{ ok: boolean }, { parentItemKey: string; note?: string }>(
      {
        query: ({ parentItemKey, note }) => ({
          url: `/workforce/lifecycle/runs/${parentItemKey}/reject`,
          method: "POST",
          body: note !== undefined ? { note } : {},
        }),
        invalidatesTags: [APPROVAL_LIST_TAG, RUN_LIST_TAG, AUDIT_LIST_TAG],
      },
    ),

    // #1418: approve or reject ONE app's held grants in a run — each app can
    // have its own approvers. The approval row clears once every app is decided.
    decideApprovalGroup: builder.mutation<
      { outcomes: unknown[] },
      { parentItemKey: string; groupKey: string; decision: "approve" | "reject" }
    >({
      query: ({ parentItemKey, groupKey, decision }) => ({
        url: `/workforce/lifecycle/runs/${encodeURIComponent(parentItemKey)}/groups/${encodeURIComponent(groupKey)}/${decision}`,
        method: "POST",
      }),
      invalidatesTags: [APPROVAL_LIST_TAG, RUN_LIST_TAG, AUDIT_LIST_TAG, PERSON_LIST_TAG],
    }),

    // #1418: Settings › Approvers — approver groups. Saving changes who may
    // decide, so both decision queues re-ask.
    getApproverGroups: builder.query<
      {
        groups: WlmApproverGroup[];
        apps: WlmApprovableApp[];
        candidates: WlmApproverCandidate[];
      },
      void
    >({
      query: () => ({ url: "/workforce/approver-groups" }),
      providesTags: [APP_APPROVER_TAG],
    }),

    createApproverGroup: builder.mutation<{ group: WlmApproverGroup }, WlmApproverGroupInput>({
      query: (body) => ({ url: "/workforce/approver-groups", method: "POST", body }),
      invalidatesTags: [APP_APPROVER_TAG, APPROVAL_LIST_TAG, ACCESS_REQUEST_LIST_TAG],
    }),

    updateApproverGroup: builder.mutation<
      { group: WlmApproverGroup },
      { id: string; body: WlmApproverGroupInput }
    >({
      query: ({ id, body }) => ({
        url: `/workforce/approver-groups/${encodeURIComponent(id)}`,
        method: "PUT",
        body,
      }),
      invalidatesTags: [APP_APPROVER_TAG, APPROVAL_LIST_TAG, ACCESS_REQUEST_LIST_TAG],
    }),

    deleteApproverGroup: builder.mutation<void, string>({
      query: (id) => ({
        url: `/workforce/approver-groups/${encodeURIComponent(id)}`,
        method: "DELETE",
      }),
      invalidatesTags: [APP_APPROVER_TAG, APPROVAL_LIST_TAG, ACCESS_REQUEST_LIST_TAG],
    }),

    // ── Audit ──
    // Tagged so LifecyclePage's Activity tab can drive itself with a plain
    // (non-lazy) useGetWorkforceAuditQuery — see getLifecycleRuns above. The
    // per-asset/per-person timeline lookups (AssetsPage, AssetDetailDrawer,
    // the Lifecycle Asset/Employee tabs) keep using the lazy trigger form;
    // this tag addition doesn't change their fetch behavior, only makes
    // their cache entries eligible for the same invalidation.
    getWorkforceAudit: builder.query<
      { data: WlmAuditEntry[]; total: number },
      WlmAuditFilter | void
    >({
      query: (params) => ({ url: "/workforce/audit", params: params ?? undefined }),
      providesTags: [AUDIT_LIST_TAG],
    }),

    // ── Service Desk seams (agent-gated: tickets.read, not WLM admin) ──
    // The agent-safe equivalent of getWorkforcePack for the ticket sidebar
    // panels — no tags: it's cheap, read-only, and RTK Query's normal
    // arg-identity caching already dedupes the one call all three panels
    // make (getTicketPackContext takes no args).
    getTicketPackContext: builder.query<WlmTicketPackContext, void>({
      query: () => ({ url: "/workforce/tickets/pack-context" }),
    }),

    getAssetOptions: builder.query<{ data: WlmAssetOption[] }, void>({
      query: () => ({ url: "/workforce/asset-options" }),
    }),

    /** `workItemId` makes a lifecycle-run ticket resolve to the person the run is about, not its admin reporter. */
    getReporterContext: builder.query<WlmReporterContext, { email: string; workItemId?: string }>({
      query: ({ email, workItemId }) => ({
        url: "/workforce/reporter-context",
        params: {
          ...(email ? { email } : {}),
          ...(workItemId ? { workItemId } : {}),
        },
      }),
    }),

    getTicketLinks: builder.query<{ data: WlmWorkItemAssetLink[] }, { workItemId: string }>({
      query: ({ workItemId }) => ({ url: "/workforce/ticket-links", params: { workItemId } }),
      providesTags: (_result, _error, arg) => [ticketLinksTag(arg.workItemId)],
    }),

    createTicketLink: builder.mutation<
      { id: string | null; alreadyLinked?: boolean },
      { workItemId: string; assetId: string }
    >({
      query: (body) => ({ url: "/workforce/ticket-links", method: "POST", body }),
      invalidatesTags: (_result, _error, arg) => [ticketLinksTag(arg.workItemId)],
    }),

    deleteTicketLink: builder.mutation<
      { success: boolean },
      { linkId: string; workItemId: string }
    >({
      query: ({ linkId }) => ({ url: `/workforce/ticket-links/${linkId}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, arg) => [ticketLinksTag(arg.workItemId)],
    }),

    getAssetTickets: builder.query<{ data: WlmTicketForAsset[] }, string>({
      query: (assetId) => ({ url: `/workforce/assets/${assetId}/tickets` }),
    }),

    // ── Lifecycle suggestions (agent-gated banner + admin Suggested list) ──
    getTicketSuggestion: builder.query<
      { suggestion: WlmLifecycleSuggestion | null },
      { workItemId: string }
    >({
      query: ({ workItemId }) => ({ url: `/workforce/tickets/${workItemId}/suggestion` }),
      providesTags: (result, _error, arg) => [
        ticketSuggestionTag(arg.workItemId),
        ...(result?.suggestion ? [suggestionIdTag(result.suggestion.id)] : []),
      ],
    }),

    dismissTicketSuggestion: builder.mutation<
      { suggestion: WlmLifecycleSuggestion },
      { workItemId: string }
    >({
      query: ({ workItemId }) => ({
        url: `/workforce/tickets/${workItemId}/suggestion/dismiss`,
        method: "POST",
      }),
      invalidatesTags: (_result, _error, arg) => [
        ticketSuggestionTag(arg.workItemId),
        SUGGESTION_LIST_TAG,
        RUN_LIST_TAG,
        AUDIT_LIST_TAG,
      ],
    }),

    getLifecycleSuggestions: builder.query<
      { data: WlmLifecycleSuggestion[]; total: number },
      WlmLifecycleSuggestionsFilter | void
    >({
      query: (params) => ({ url: "/workforce/lifecycle/suggestions", params: params ?? undefined }),
      providesTags: [SUGGESTION_LIST_TAG],
    }),

    // Tickets tab "Assets" filter: tickets that have linked assets.
    getAssetLinkedTickets: builder.query<
      { data: WlmAssetLinkedTicket[]; total: number },
      { page?: number; pageSize?: number } | void
    >({
      query: (params) => ({
        url: "/workforce/lifecycle/asset-tickets",
        params: params ?? undefined,
      }),
    }),

    // ── Licenses & subscriptions ──
    getLicenses: builder.query<{ data: WlmLicense[]; total: number }, WlmLicenseListFilter | void>({
      query: (filter) => ({ url: "/workforce/licenses", params: filter ?? undefined }),
      providesTags: [LICENSE_LIST_TAG],
    }),

    createLicense: builder.mutation<WlmLicense, WlmLicenseInput>({
      query: (body) => ({ url: "/workforce/licenses", method: "POST", body }),
      invalidatesTags: [LICENSE_LIST_TAG],
    }),

    updateLicense: builder.mutation<WlmLicense, { id: string; body: WlmLicensePatch }>({
      query: ({ id, body }) => ({ url: `/workforce/licenses/${id}`, method: "PATCH", body }),
      invalidatesTags: (_result, _error, arg) => [LICENSE_LIST_TAG, licenseIdTag(arg.id)],
    }),

    deleteLicense: builder.mutation<{ success: boolean; message: string }, string>({
      query: (id) => ({ url: `/workforce/licenses/${id}`, method: "DELETE" }),
      invalidatesTags: (_result, _error, id) => [LICENSE_LIST_TAG, licenseIdTag(id)],
    }),

    // Per-license-id tag — a license card's expanded seat list queries by
    // licenseId, so grant/revoke on that license refetch just this list
    // (plus LIST, since seatsUsed/seatsFree on the card itself change too).
    getLicenseSeats: builder.query<{ seats: WlmLicenseSeat[]; revokedCount: number }, string>({
      query: (licenseId) => ({ url: `/workforce/licenses/${licenseId}/seats` }),
      providesTags: (_result, _error, licenseId) => [licenseIdTag(licenseId)],
    }),

    // agentId is optional caller-supplied metadata (the granted party's
    // agent, when known) purely for cache invalidation — mirrors
    // assignAsset/unassignAsset — so the person's profile Subscriptions
    // card refetches without a hard reload.
    grantSeat: builder.mutation<
      WlmLicenseSeat,
      { licenseId: string; partyId: string; agentId?: string; confirmOffboarding?: boolean }
    >({
      query: ({ licenseId, partyId, confirmOffboarding }) => ({
        url: `/workforce/licenses/${licenseId}/seats`,
        method: "POST",
        body: { partyId, ...(confirmOffboarding ? { confirmOffboarding } : {}) },
      }),
      invalidatesTags: (_result, _error, arg) => [
        LICENSE_LIST_TAG,
        licenseIdTag(arg.licenseId),
        ...(arg.agentId ? [{ type: "WorkforcePerson" as const, id: arg.agentId }] : []),
      ],
    }),

    revokeSeat: builder.mutation<
      { success: boolean; message: string },
      { licenseId: string; partyId: string; agentId?: string }
    >({
      query: ({ licenseId, partyId }) => ({
        url: `/workforce/licenses/${licenseId}/seats/${partyId}`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, arg) => [
        LICENSE_LIST_TAG,
        licenseIdTag(arg.licenseId),
        ...(arg.agentId ? [{ type: "WorkforcePerson" as const, id: arg.agentId }] : []),
      ],
    }),

    // ── Access grant map (Wave 2) ──
    getAccessGrants: builder.query<WlmAccessGrant[], void>({
      query: () => ({ url: "/workforce/access-grants" }),
      transformResponse: (res: { grants: WlmAccessGrantWire[] }) =>
        res.grants.map(camelizeAccessGrant),
      providesTags: [ACCESS_GRANT_LIST_TAG],
    }),

    getGrantableApps: builder.query<WlmGrantableApp[], void>({
      query: () => ({ url: "/workforce/access-grants/apps" }),
      transformResponse: (res: { apps: WlmGrantableApp[] }) => res.apps,
    }),

    createAccessGrant: builder.mutation<WlmAccessGrant, WlmAccessGrantInput>({
      query: (body) => ({ url: "/workforce/access-grants", method: "POST", body }),
      transformResponse: (res: { grant: WlmAccessGrantWire }) => camelizeAccessGrant(res.grant),
      invalidatesTags: [ACCESS_GRANT_LIST_TAG],
    }),

    updateAccessGrant: builder.mutation<WlmAccessGrant, { id: string; body: WlmAccessGrantInput }>({
      query: ({ id, body }) => ({
        url: `/workforce/access-grants/${id}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (res: { grant: WlmAccessGrantWire }) => camelizeAccessGrant(res.grant),
      invalidatesTags: [ACCESS_GRANT_LIST_TAG],
    }),

    bulkImportAccessGrants: builder.mutation<{ created: number }, WlmAccessGrantImportRow[]>({
      query: (rows) => ({
        url: "/workforce/access-grants/bulk",
        method: "POST",
        body: { rows },
      }),
      invalidatesTags: [ACCESS_GRANT_LIST_TAG],
    }),

    deleteAccessGrant: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/workforce/access-grants/${id}`, method: "DELETE" }),
      invalidatesTags: [ACCESS_GRANT_LIST_TAG],
    }),

    // ── Directory / IdP inbound sync ──
    getDirectorySyncStatus: builder.query<WlmDirectorySyncStatus, void>({
      query: () => ({ url: "/workforce/directory-sync/status" }),
      providesTags: [DIRECTORY_SYNC_TAG],
    }),

    getSyncSuggestions: builder.query<{ suggestions: WlmSyncSuggestion[] }, void>({
      query: () => ({ url: "/workforce/directory-sync/suggestions" }),
      providesTags: [DIRECTORY_SYNC_TAG],
    }),

    dismissSyncSuggestion: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({
        url: `/workforce/directory-sync/suggestions/${id}/dismiss`,
        method: "POST",
      }),
      invalidatesTags: [DIRECTORY_SYNC_TAG],
    }),

    // Invalidates the same tags startOffboarding does (minus the per-agent
    // tag — this response doesn't carry the agent id) so the roster/pack
    // member counts and lifecycle history feeds refresh when the kickoff
    // succeeds, not just the sync status/suggestions list.
    offboardSyncSuggestion: builder.mutation<WlmDirectorySyncOffboardResult, string>({
      query: (id) => ({
        url: `/workforce/directory-sync/suggestions/${id}/offboard`,
        method: "POST",
      }),
      invalidatesTags: [
        DIRECTORY_SYNC_TAG,
        PERSON_LIST_TAG,
        PACK_STATE_TAG,
        RUN_LIST_TAG,
        AUDIT_LIST_TAG,
      ],
    }),

    // ── Dashboard ──
    // Optional { departmentId, from, to } scope the server-side rollups. RTK
    // Query already keys the cache by the serialized arg, so each filter combo
    // is a distinct cache entry; the DASHBOARD_TAG (with a per-arg id) keeps a
    // mutation able to invalidate every combo at once via the shared type.
    getWorkforceDashboardSummary: builder.query<WlmDashboardSummary, WlmDashboardFilters | void>({
      query: (arg) => ({
        url: "/workforce/dashboard/summary",
        params: dashboardParams(arg || undefined),
      }),
      providesTags: (_r, _e, arg) => [
        { type: "WorkforceDashboard" as const, id: dashboardCacheId(arg || undefined) },
      ],
    }),

    // `days` is optional — the server defaults to 90 when omitted (see
    // dashboardTrendsQuerySchema). departmentId/from/to mirror the summary
    // filters; the server folds `days` into the from/to window.
    getWorkforceDashboardTrends: builder.query<
      WlmTrendPoint[],
      ({ days?: number } & WlmDashboardFilters) | void
    >({
      query: (arg) => ({
        url: "/workforce/dashboard/trends",
        params: dashboardParams(arg || undefined, arg?.days),
      }),
      providesTags: (_r, _e, arg) => [
        { type: "WorkforceDashboard" as const, id: dashboardCacheId(arg || undefined, arg?.days) },
      ],
    }),

    // ── Compliance reports (Task 3-4) ──
    getAccessHoldings: builder.query<WlmAccessHolding[], void>({
      query: () => ({ url: "/workforce/reports/access-holdings" }),
      transformResponse: (res: { data: WlmAccessHolding[] }) => res.data,
      providesTags: [REPORT_TAG],
    }),

    getAccessChangeHistory: builder.query<WlmAccessChange[], { from?: string; to?: string } | void>(
      {
        query: (arg) => ({
          url: "/workforce/reports/access-history",
          params: arg?.from || arg?.to ? { from: arg.from, to: arg.to } : undefined,
        }),
        transformResponse: (res: { data: WlmAccessChange[] }) => res.data,
        providesTags: [REPORT_TAG],
      },
    ),

    getLicenseCompliance: builder.query<WlmLicenseCompliance[], void>({
      query: () => ({ url: "/workforce/reports/license-compliance" }),
      transformResponse: (res: { data: WlmLicenseCompliance[] }) => res.data,
      providesTags: [REPORT_TAG],
    }),

    getLicenseSeatHolders: builder.query<WlmSeatHolder[], void>({
      query: () => ({ url: "/workforce/reports/seat-holders" }),
      transformResponse: (res: { data: WlmSeatHolder[] }) => res.data,
      providesTags: [REPORT_TAG],
    }),

    getLicenseReconciliation: builder.query<WlmLicenseReconciliation[], void>({
      query: () => ({ url: "/workforce/reports/license-reconciliation" }),
      transformResponse: (res: { data: WlmLicenseReconciliation[] }) => res.data,
      providesTags: [REPORT_TAG],
    }),

    // ── Access certification (Wave 4) ──
    getCertCampaigns: builder.query<WlmCertCampaign[], void>({
      query: () => ({ url: "/workforce/certifications" }),
      transformResponse: (res: { data: WlmCertCampaign[] }) => res.data,
      providesTags: [CERT_CAMPAIGN_LIST_TAG],
    }),

    createCertCampaign: builder.mutation<WlmCertCampaign, { name: string }>({
      query: (body) => ({ url: "/workforce/certifications", method: "POST", body }),
      transformResponse: (res: { data: WlmCertCampaign }) => res.data,
      invalidatesTags: [CERT_CAMPAIGN_LIST_TAG],
    }),

    getCertCampaign: builder.query<WlmCertCampaignDetail, string>({
      query: (id) => ({ url: `/workforce/certifications/${id}` }),
      transformResponse: (res: { data: WlmCertCampaignDetail }) => res.data,
      providesTags: (_result, _error, id) => [certCampaignIdTag(id)],
    }),

    // Invalidates the campaign's own detail tag (so its item list + decision
    // counts refresh) and the list tag (so the list view's progress column
    // reflects the new attested/revoked count without a reload).
    decideCertItem: builder.mutation<
      WlmCertItem,
      { campaignId: string; itemId: string; decision: "attested" | "revoked" }
    >({
      query: ({ campaignId, itemId, decision }) => ({
        url: `/workforce/certifications/${campaignId}/items/${itemId}/decision`,
        method: "POST",
        body: { decision },
      }),
      transformResponse: (res: { data: WlmCertItem }) => res.data,
      invalidatesTags: (_result, _error, { campaignId }) => [
        certCampaignIdTag(campaignId),
        CERT_CAMPAIGN_LIST_TAG,
      ],
    }),

    // ── Separation of duties (2e) ──
    getSodRoleCatalog: builder.query<WlmRoleCatalogEntry[], void>({
      query: () => ({ url: "/workforce/sod/roles" }),
      transformResponse: (res: { roles: WlmRoleCatalogEntry[] }) => res.roles,
    }),

    getSodRules: builder.query<WlmSodRule[], void>({
      query: () => ({ url: "/workforce/sod/rules" }),
      transformResponse: (res: { rules: WlmSodRule[] }) => res.rules,
      providesTags: [SOD_RULE_LIST_TAG],
    }),

    createSodRule: builder.mutation<WlmSodRule, WlmSodRuleInput>({
      query: (body) => ({ url: "/workforce/sod/rules", method: "POST", body }),
      transformResponse: (res: { rule: WlmSodRule }) => res.rule,
      // A new rule can change the violation set, so invalidate the report tag too.
      invalidatesTags: [SOD_RULE_LIST_TAG, REPORT_TAG],
    }),

    deleteSodRule: builder.mutation<{ ok: boolean }, string>({
      query: (id) => ({ url: `/workforce/sod/rules/${id}`, method: "DELETE" }),
      invalidatesTags: [SOD_RULE_LIST_TAG, REPORT_TAG],
    }),

    getSodViolations: builder.query<WlmSodViolation[], void>({
      query: () => ({ url: "/workforce/sod/violations" }),
      transformResponse: (res: { data: WlmSodViolation[] }) => res.data,
      providesTags: [REPORT_TAG],
    }),

    // ── Employee self-service (5a) ──
    // Non-admin, self-scoped. A 404 means "not a workforce person here" — the
    // portal uses that to hide the My-workspace surface for external customers.
    getMyWorkspace: builder.query<WlmMyWorkspace, void>({
      query: () => ({ url: "/workforce/me" }),
      transformResponse: (res: { data: WlmMyWorkspace }) => res.data,
      providesTags: [{ type: "WorkforceMe", id: "SELF" }],
    }),

    // ── Employee self-service request (5b) ──
    getMyRequestCatalog: builder.query<WlmRequestCatalog, void>({
      query: () => ({ url: "/workforce/me/catalog" }),
      transformResponse: (res: { catalog: WlmRequestCatalog }) => res.catalog,
    }),

    getMyRequests: builder.query<WlmAccessRequest[], void>({
      query: () => ({ url: "/workforce/me/requests" }),
      transformResponse: (res: { data: WlmAccessRequest[] }) => res.data,
      providesTags: [{ type: "WorkforceMyRequest", id: "LIST" }],
    }),

    createMyRequest: builder.mutation<WlmAccessRequest, WlmSelfAccessRequestInput>({
      query: (body) => ({ url: "/workforce/me/requests", method: "POST", body }),
      transformResponse: (res: { data: WlmAccessRequest }) => res.data,
      invalidatesTags: [{ type: "WorkforceMyRequest", id: "LIST" }],
    }),

    // ── Employee self-service onboarding (5c) ──
    getMyOnboarding: builder.query<WlmMyOnboarding, void>({
      query: () => ({ url: "/workforce/me/onboarding" }),
      transformResponse: (res: { data: WlmMyOnboarding }) => res.data,
      providesTags: [{ type: "WorkforceMyOnboarding", id: "SELF" }],
    }),

    completeMyOnboardingTask: builder.mutation<WlmMyOnboardingTask, string>({
      query: (id) => ({ url: `/workforce/me/onboarding/${id}/complete`, method: "POST" }),
      transformResponse: (res: { data: WlmMyOnboardingTask }) => res.data,
      invalidatesTags: [{ type: "WorkforceMyOnboarding", id: "SELF" }],
    }),

    reopenMyOnboardingTask: builder.mutation<WlmMyOnboardingTask, string>({
      query: (id) => ({ url: `/workforce/me/onboarding/${id}/reopen`, method: "POST" }),
      transformResponse: (res: { data: WlmMyOnboardingTask }) => res.data,
      invalidatesTags: [{ type: "WorkforceMyOnboarding", id: "SELF" }],
    }),

    // ── Manager hub (5d) ──
    // A 404 means "not a manager" — the portal uses that to hide the My-team surface.
    getMyTeam: builder.query<WlmMyTeam, void>({
      query: () => ({ url: "/workforce/me/team" }),
      transformResponse: (res: { data: WlmMyTeam }) => res.data,
      providesTags: [{ type: "WorkforceMyTeam", id: "SELF" }],
    }),

    getTeamMember: builder.query<WlmTeamMemberWorkspace, string>({
      query: (partyId) => ({ url: `/workforce/me/team/members/${partyId}` }),
      transformResponse: (res: { data: WlmTeamMemberWorkspace }) => res.data,
      providesTags: (_r, _e, partyId) => [{ type: "WorkforceMyTeam", id: partyId }],
    }),

    getTeamApprovals: builder.query<WlmAccessRequest[], void>({
      query: () => ({ url: "/workforce/me/team/approvals" }),
      transformResponse: (res: { data: WlmAccessRequest[] }) => res.data,
      providesTags: [{ type: "WorkforceMyTeam", id: "APPROVALS" }],
    }),

    decideTeamApproval: builder.mutation<
      WlmAccessRequest,
      { id: string; decision: "approve" | "reject" }
    >({
      query: ({ id, decision }) => ({
        url: `/workforce/me/team/approvals/${id}/decide`,
        method: "POST",
        body: { decision },
      }),
      transformResponse: (res: { data: WlmAccessRequest }) => res.data,
      // A decision changes the approvals list AND the report's holdings.
      invalidatesTags: [
        { type: "WorkforceMyTeam", id: "APPROVALS" },
        { type: "WorkforceMyTeam", id: "SELF" },
      ],
    }),

    // A manager raises an access request on behalf of a direct report.
    createTeamMemberRequest: builder.mutation<
      WlmAccessRequest,
      { partyId: string; body: WlmSelfAccessRequestInput }
    >({
      query: ({ partyId, body }) => ({
        url: `/workforce/me/team/members/${partyId}/requests`,
        method: "POST",
        body,
      }),
      transformResponse: (res: { data: WlmAccessRequest }) => res.data,
      invalidatesTags: (_r, _e, arg) => [
        { type: "WorkforceMyTeam", id: arg.partyId },
        { type: "WorkforceMyTeam", id: "SELF" },
      ],
    }),

    // ── Access request catalog (2c) ──
    getAccessRequestCatalog: builder.query<WlmRequestCatalog, void>({
      query: () => ({ url: "/workforce/access-requests/catalog" }),
      transformResponse: (res: { catalog: WlmRequestCatalog }) => res.catalog,
    }),

    getAccessRequests: builder.query<WlmAccessRequest[], void>({
      query: () => ({ url: "/workforce/access-requests" }),
      transformResponse: (res: { data: WlmAccessRequest[] }) => res.data,
      providesTags: [ACCESS_REQUEST_LIST_TAG],
    }),

    createAccessRequest: builder.mutation<WlmAccessRequest, WlmAccessRequestInput>({
      query: (body) => ({ url: "/workforce/access-requests", method: "POST", body }),
      transformResponse: (res: { data: WlmAccessRequest }) => res.data,
      invalidatesTags: [ACCESS_REQUEST_LIST_TAG],
    }),

    decideAccessRequest: builder.mutation<
      WlmAccessRequest,
      { id: string; decision: "approve" | "reject" }
    >({
      query: ({ id, decision }) => ({
        url: `/workforce/access-requests/${id}/decision`,
        method: "POST",
        body: { decision },
      }),
      transformResponse: (res: { data: WlmAccessRequest }) => res.data,
      // A fulfilled request can change seat usage + person access, so refresh
      // those lists alongside the request list.
      invalidatesTags: [ACCESS_REQUEST_LIST_TAG, LICENSE_LIST_TAG, PERSON_LIST_TAG],
    }),

    // ── Org chart ──
    getOrgRoots: builder.query<OrgChartNode[], OrgChartFilters | void>({
      query: (f) => ({ url: "/workforce/org/roots", params: orgFilterParams(f || undefined) }),
      transformResponse: (r: { data: OrgChartNode[] }) => r.data,
      providesTags: [ORG_TAG],
    }),
    getOrgFocus: builder.query<OrgFocusResponse, { partyId: string }>({
      query: ({ partyId }) => ({ url: `/workforce/org/focus/${partyId}` }),
      providesTags: [ORG_TAG],
    }),
    getOrgChildren: builder.query<OrgChartNode[], { partyId: string; filters?: OrgChartFilters }>({
      query: ({ partyId, filters }) => ({
        url: `/workforce/org/children/${partyId}`,
        params: orgFilterParams(filters),
      }),
      transformResponse: (r: { data: OrgChartNode[] }) => r.data,
      providesTags: [ORG_TAG],
    }),
    searchOrg: builder.query<OrgChartNode[], { q: string }>({
      query: ({ q }) => ({ url: "/workforce/org/search", params: { q } }),
      transformResponse: (r: { data: OrgChartNode[] }) => r.data,
    }),
    reassignManager: builder.mutation<unknown, { partyId: string; managerPartyId: string | null }>({
      query: ({ partyId, managerPartyId }) => ({
        url: `/workforce/people/${partyId}/manager`,
        method: "PUT",
        body: { managerPartyId },
      }),
      invalidatesTags: [...REPORTING_LINE_TAGS],
    }),
    getMyOrgFocus: builder.query<OrgFocusResponse, void>({
      query: () => ({ url: "/workforce/me/org/focus" }),
      providesTags: [ORG_TAG],
    }),
    getMyOrgChildren: builder.query<OrgChartNode[], { partyId: string }>({
      query: ({ partyId }) => ({ url: `/workforce/me/org/children/${partyId}` }),
      transformResponse: (r: { data: OrgChartNode[] }) => r.data,
    }),
  }),
});

export const {
  useGetWorkforcePackQuery,
  useInstallPackMutation,
  useResetPackMutation,
  useUpdateWorkforceSettingsMutation,
  useGetWlmLocationsQuery,
  useCreateWlmLocationMutation,
  useUpdateWlmLocationMutation,
  useSetPersonLocationMutation,
  useCreateDepartmentMutation,
  useUpdateDepartmentMutation,
  useDeleteDepartmentMutation,
  useAddDepartmentRoleMutation,
  useDeleteDepartmentRoleMutation,
  useAddOnboardingTaskMutation,
  useUpdateOnboardingTaskMutation,
  useDeleteOnboardingTaskMutation,
  useGetTicketTemplatesQuery,
  useCreateTicketTemplateMutation,
  useUpdateTicketTemplateMutation,
  useDeleteTicketTemplateMutation,
  useAttachDeptTicketTemplateMutation,
  useDetachDeptTicketTemplateMutation,
  useCreateAssetTypeMutation,
  useUpdateAssetTypeMutation,
  useDeleteAssetTypeMutation,
  useAddAssetTypeAttributeMutation,
  useUpdateAssetTypeAttributeMutation,
  useDeleteAssetTypeAttributeMutation,
  useGetAssetsQuery,
  useGetAssetDetailQuery,
  useGetAssetGraphQuery,
  useCreateAssetMutation,
  useBulkImportAssetsMutation,
  useUpdateAssetMutation,
  useDeleteAssetMutation,
  useUploadAssetAttributeImageMutation,
  useDeleteAssetAttributeImageMutation,
  useUploadAssetTypeImageMutation,
  useDeleteAssetTypeImageMutation,
  useAssignAssetMutation,
  useMarkAssetFixedMutation,
  useUnassignAssetMutation,
  useGetPeopleQuery,
  useGetAllPeopleQuery,
  useLazyGetPeopleQuery,
  useGetPersonProfileQuery,
  useGetPersonAppAccessQuery,
  useUpdatePersonDetailsMutation,
  useSetPersonManagerMutation,
  useGetPersonOnboardingQuery,
  useAddPersonOnboardingTaskMutation,
  useUpdatePersonOnboardingTaskMutation,
  useDeletePersonOnboardingTaskMutation,
  useAddEmploymentMutation,
  useSyncPeopleMutation,
  useStartOnboardingMutation,
  useLazyPreviewOnboardingGrantsQuery,
  useStartOffboardingMutation,
  useMovePreviewMutation,
  useExecuteMoveMutation,
  useLazyGetWorkforceAuditQuery,
  useGetWorkforceAuditQuery,
  useGetLifecycleRunsQuery,
  useLazyGetLifecycleRunsQuery,
  useGetLifecycleApprovalsQuery,
  useApproveLifecycleRunMutation,
  useRejectLifecycleRunMutation,
  useDecideApprovalGroupMutation,
  useGetApproverGroupsQuery,
  useCreateApproverGroupMutation,
  useUpdateApproverGroupMutation,
  useDeleteApproverGroupMutation,
  useLazyGetAssetLinkedTicketsQuery,
  useGetTicketPackContextQuery,
  useGetAssetOptionsQuery,
  useGetReporterContextQuery,
  useGetTicketLinksQuery,
  useCreateTicketLinkMutation,
  useDeleteTicketLinkMutation,
  useGetAssetTicketsQuery,
  useGetTicketSuggestionQuery,
  useDismissTicketSuggestionMutation,
  useGetLifecycleSuggestionsQuery,
  useLazyGetLifecycleSuggestionsQuery,
  useGetLicensesQuery,
  useCreateLicenseMutation,
  useUpdateLicenseMutation,
  useDeleteLicenseMutation,
  useGetLicenseSeatsQuery,
  useGrantSeatMutation,
  useRevokeSeatMutation,
  useGetAccessGrantsQuery,
  useGetGrantableAppsQuery,
  useCreateAccessGrantMutation,
  useUpdateAccessGrantMutation,
  useBulkImportAccessGrantsMutation,
  useDeleteAccessGrantMutation,
  useGetDirectorySyncStatusQuery,
  useGetSyncSuggestionsQuery,
  useDismissSyncSuggestionMutation,
  useOffboardSyncSuggestionMutation,
  useGetWorkforceDashboardSummaryQuery,
  useGetWorkforceDashboardTrendsQuery,
  useGetAccessHoldingsQuery,
  useGetAccessChangeHistoryQuery,
  useGetLicenseComplianceQuery,
  useGetLicenseSeatHoldersQuery,
  useGetLicenseReconciliationQuery,
  useGetCertCampaignsQuery,
  useCreateCertCampaignMutation,
  useGetCertCampaignQuery,
  useDecideCertItemMutation,
  useGetSodRoleCatalogQuery,
  useGetSodRulesQuery,
  useCreateSodRuleMutation,
  useDeleteSodRuleMutation,
  useGetSodViolationsQuery,
  useGetAccessRequestCatalogQuery,
  useGetAccessRequestsQuery,
  useCreateAccessRequestMutation,
  useDecideAccessRequestMutation,
  useGetMyWorkspaceQuery,
  useGetMyRequestCatalogQuery,
  useGetMyRequestsQuery,
  useCreateMyRequestMutation,
  useGetMyOnboardingQuery,
  useCompleteMyOnboardingTaskMutation,
  useReopenMyOnboardingTaskMutation,
  useGetMyTeamQuery,
  useGetTeamMemberQuery,
  useGetTeamApprovalsQuery,
  useDecideTeamApprovalMutation,
  useCreateTeamMemberRequestMutation,
  useGetOrgRootsQuery,
  useGetOrgFocusQuery,
  useGetOrgChildrenQuery,
  useLazyGetOrgChildrenQuery,
  useSearchOrgQuery,
  useLazySearchOrgQuery,
  useReassignManagerMutation,
  useGetMyOrgFocusQuery,
  useLazyGetMyOrgChildrenQuery,
} = workforceApi;
